/**
 * FieldConfig conditional visibility usage:
 *
 * - showWhen: { field: string, value: any }  // Show field only when field === value
 * - hideWhen: { field: string, value: any }  // Hide field when field === value (takes precedence if both present)
 *
 * Example:
 *   {
 *     name: 'numberOfAxisPerCar',
 *     type: 'number',
 *     label: 'number_of_axis_per_car',
 *     required: true,
 *     defaultValue: 4,
 *     unit: '-',
 *     min: 0,
 *     max: 20,
 *     symbol: 'Np',
 *     isConvertible: false,
 *     showWhen: { field: 'railwayToDesign', value: 'cargo_train' },
 *     // OR
 *     hideWhen: { field: 'railwayToDesign', value: 'mixed_train' },
 *   }
 *
 * Rendering logic must be updated to support hideWhen:
 *   - If hideWhen matches, field is hidden (regardless of showWhen)
 *   - If showWhen matches (and hideWhen does not), field is shown
 *   - If neither present, field is always shown
 */
import { conversionLabel } from '../conversion';
import { getBallastOptions } from './BallastCbrDatabase';
import { ProjectTypeJson, FieldConfig, GeoGridTypeJson } from './ValidationService';
import { TGeogridProductData } from '../data/IUnifiedProductData';
import { EProjectType } from '../enum/project';
import { materialDatabaseService } from './MaterialDatabaseService';
import TrafficClassificationService from './TrafficClassificationService';
import * as BeMaccaferriStoreHelper from '../api/store/BeMaccaferriStoreHelper';
import { IGeogridFamily } from '../api/IApiResponse';
import { logDebug, logError, logWarn } from '../utils/utils';
import { ProductDataService } from './ProductDataService';
import { railwayPdfConfig } from 'app/components/PDF/templates/configs/railwayConfig';

export const SUBGRADE_SOIL_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'lean_clay', label: 'Lean clay' },
    { value: 'fat_clay', label: 'Fat clay' },
    { value: 'elastic_silt', label: 'Elastic silt' },
    { value: 'silt', label: 'Silt' },
];

export const getSubgradeSoilTypeLabel = (value: string): string => SUBGRADE_SOIL_TYPE_OPTIONS.find(option => option.value === value)?.label ?? value;

/**
 * Determines the project method based on project data
 *
 * Method Detection Logic:
 * - Container Yard/Parking Deck: 'bcr' if has bcr data, else 'static'
 * - Airport/Paved: 'asphalt'
 * - Unpaved: 'giroud' if has giroud data, else 'leng'
 * - Railways: 'default'
 * - Geogrid standalone: '' (no method)
 *
 * @param projectData - The project data object
 * @returns The detected method string
 */
export const getMethodByProjectData = (projectData: unknown): string => {
    const data = projectData as { type?: EProjectType; method?: string; bcr?: unknown; giroud?: unknown };
    const type: EProjectType | undefined = data.type;

    switch (type) {
        case EProjectType.container_yard:
        case EProjectType.parking_deck:
            // CRITICAL FIX: Check 'method' field first (backend sets this explicitly)
            // Fall back to checking nested keys for backward compatibility
            if (data.method) {
                return data.method; // Return explicit method from backend ('bcr' or 'static')
            }
            return data.bcr ? 'bcr' : 'static';

        case EProjectType.airport:
        case EProjectType.paved:
            return 'asphalt';

        case EProjectType.unpaved:
            // CRITICAL FIX: Check 'method' field first (backend sets this explicitly)
            if (data.method) {
                return data.method; // Return explicit method from backend ('giroud' or 'leng')
            }
            return data.giroud ? 'giroud' : 'leng';

        case EProjectType.railways:
            return 'default';

        case EProjectType.geogrid:
            return ''; // Geogrid standalone has no method

        default:
            return ''; // Unknown or missing type
    }
};

/**
 * Determines project type and method from project data
 *
 * @deprecated Use `getMethodByProjectData()` instead for method detection.
 * For type detection, use `ProjectApiService.transformProjectFromBackend()` which is the authoritative source.
 *
 * This function has a problematic fallback (type || container_yard) that can cause incorrect type detection.
 * It will be removed in a future version.
 *
 * @param projectData - The project data object
 * @returns Object with type and method
 */
export const getDataTypeByProject = (projectData: unknown) => {
    console.warn(
        '⚠️ getDataTypeByProject() is deprecated. ' + 'Use getMethodByProjectData() for method detection. ' + 'For type detection, transformProjectFromBackend() is the authoritative source.'
    );

    const data = projectData as { type?: EProjectType };
    const type: EProjectType | undefined = data.type;
    const method = getMethodByProjectData(projectData);

    // PROBLEMATIC: This fallback to container_yard can cause incorrect type detection
    // Type should be determined by transformProjectFromBackend() instead
    return { type: type || EProjectType.container_yard, method };
};

const getProjectSchema = (projectType: { type: string; method: string }): ProjectTypeJson | undefined => {
    if (projectType.type === EProjectType.container_yard && projectType.method === 'bcr') return getContainerYardBcr(projectType);
    else if (projectType.type === EProjectType.container_yard && projectType.method === 'static') return getContainerYardStatic(projectType);
    else if (projectType.type === EProjectType.parking_deck && projectType.method === 'bcr') return getParkingDeckBcr(projectType);
    else if (projectType.type === EProjectType.parking_deck && projectType.method === 'static') return getParkingDeckStatic(projectType);
    else if (projectType.type === EProjectType.paved && projectType.method === 'asphalt') return getPavedAsphalt(projectType);
    else if (projectType.type === EProjectType.unpaved && projectType.method === 'giroud') return getUnpavedGiroud(projectType);
    else if (projectType.type === EProjectType.unpaved && projectType.method === 'leng') return getUnpavedLeng(projectType);
    else if (projectType.type === EProjectType.railways && projectType.method === 'default') return getRailwayDefault(projectType);
    else if (projectType.type === EProjectType.airport && projectType.method === 'asphalt') return getAirportDefault(projectType);
    return undefined;
};

/**
 * Synchronous schema lookup for callers that cannot await (e.g. transformProjectFromBackend).
 * `schema` is absent for unknown type/method combinations instead of throwing.
 */
export const resolveProjectSchemas = (type: string, method: string): { schema?: ProjectTypeJson; geoGridSchema: GeoGridTypeJson } => ({
    schema: getProjectSchema({ type, method }),
    geoGridSchema: getGeoGridJson(!method, type),
});

/**
 * Ottiene il JSON di configurazione del progetto per un tipo e metodo specifici
 */
export const getProjectTypeJson = async (projectType: { type: string; method: string }): Promise<ProjectTypeJson> => {
    const schema = getProjectSchema(projectType);
    if (schema) return schema;
    throw new Error(`No project type configuration found for ${projectType.type}/${projectType.method}`);
};

/**
 * Imposta un campo nell'oggetto del progetto (ora annidato)
 */

export const updateDataForNewMethod = (currentData: unknown, oldMethod: string, newMethod: string, initialDataForNewMethod: Record<string, unknown>): unknown => {
    // Create a shallow copy to avoid modifying the original object directly
    const updatedData = { ...(currentData as Record<string, unknown>) };

    // Remove the data associated with the old method, if it exists
    if (oldMethod && updatedData.hasOwnProperty(oldMethod)) {
        delete updatedData[oldMethod];
    }

    // Add the initial data structure for the new method (already nested)
    updatedData[newMethod] = initialDataForNewMethod;

    return updatedData;
};

const getUnpavedGiroud = (projectType: { type: string; method: string }): ProjectTypeJson => {
    return {
        projectType: projectType.type,
        projectMethod: projectType.method,
        accordionTitles: {
            geoTextile: 'geotextile_selection_question', // Titolo personalizzato per l'accordion geotextile
            load: 'load_geometry',
            subgrade: 'soil_properties',
            geoGrid: 'select_geogrid_family',
        },
        fields: {
            projectSettings: [
                {
                    name: 'calculationMethod',
                    type: 'string',
                    renderAs: 'select',
                    label: 'calculation_method',
                    required: true,
                    defaultValue: 'giroud',
                    options: [
                        { value: 'giroud', label: 'Giroud-Han' },
                        { value: 'leng', label: 'Leng-Gabr' },
                    ],
                    isConvertible: true,
                },
            ],

            load: [
                { name: 'wheelLoad', type: 'number', label: 'Wheel load', required: true, defaultValue: 75, unit: conversionLabel.kN_lbf, symbol: 'P', isConvertible: true, min: 0, max: 1000 },
                { name: 'tyreInflationPressure', type: 'number', label: 'Tyre inflation pressure', required: true, defaultValue: 500, unit: conversionLabel.kPa, symbol: 'p', isConvertible: true, min: 0, max: 1000 },
                { name: 'esal', type: 'number', label: 'Equivalent Standard Axle Load (ESAL)', required: true, defaultValue: 80, unit: conversionLabel.kN_lbf, symbol: 'Pa', isConvertible: true, min: 0, max: 1000 },
                { name: 'numPassages', type: 'number', label: 'Number of passages of design axle load P', required: true, defaultValue: 1000, unit: '', symbol: 'Np', isConvertible: false, min: 0, max: 1000000000 },
                { name: 'allowableRutDepth', type: 'number', label: 'Allowable rut depth', required: true, defaultValue: 50, unit: conversionLabel.mm_in, symbol: 's', isConvertible: true, min: 0, max: 500 },
            ],

            subgrade: [
                {
                    name: 'subgradeInputMethod',
                    label: 'subgrade_input_method_label',
                    type: 'string',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'cbr', label: 'subgrade_option_cbr' },
                        { value: 'modulus', label: 'subgrade_option_modulus' },
                    ],
                    defaultValue: 'cbr',
                    required: true,
                },
                // Base Course fields - CBR mode
                {
                    name: 'cbrBaseCourse',
                    type: 'number',
                    label: 'cbr_base_course_label', // "CBR of base course"
                    required: false,
                    defaultValue: 15.0,
                    unit: ['%'],
                    isConvertible: false,
                    min: 0,
                    max: 30,
                    symbol: 'CBRbc',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                {
                    name: 'cbrSubgrade',
                    type: 'number',
                    label: 'cbr_subgrade_label', // "CBR of subgrade"
                    required: false,
                    defaultValue: 2.0,
                    unit: ['%'],
                    isConvertible: false,
                    min: 0,
                    max: 3,
                    symbol: 'CBRsg ≤ 3',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                // Base Course fields - Ev2 mode
                {
                    name: 'ev2BaseCourse',
                    type: 'number',
                    label: 'ev2_base_course_label', // "Ev2 of base course"
                    required: false,
                    defaultValue: 100.0,
                    unit: ['MPa'],
                    isConvertible: false,
                    min: 0,
                    max: 1000,
                    symbol: 'Ev2bc',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'modulus',
                    },
                },
                {
                    name: 'ev2Subgrade',
                    type: 'number',
                    label: 'ev2_subgrade_label', // "Ev2 of subgrade"
                    required: false,
                    defaultValue: 40.0,
                    unit: ['MPa'],
                    isConvertible: false,
                    min: 0,
                    max: 500,
                    symbol: 'Ev2sg ≤ 3',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'modulus',
                    },
                },
            ],
            geoGrid: [
                {
                    name: 'selectedGeogrid',
                    type: 'string',
                    label: 'selected_geogrid_product',
                    renderAs: 'select',
                    // TODO: Phase 3/4 - Use ConfigurationService.getGeogridDefaults().unpaved
                    // Source of Truth: pavement-database.json designParameters.geogridDefaults.unpaved
                    defaultValue: 'MACGRID WG 3S',
                    required: false,
                    optionsSource: 'unpavedGiroudData',
                },
            ],
            geoTextile: [
                {
                    name: 'selectedGeotextile',
                    type: 'string',
                    label: 'selected_geotextile_product',
                    renderAs: 'select',
                    defaultValue: '',
                    required: false,
                    optionsSource: 'unpavedGiroudGeotextiles',
                },
            ],
        },
    };
};

const getUnpavedLeng = (projectType: { type: string; method: string }): ProjectTypeJson => {
    return {
        projectType: projectType.type,
        projectMethod: projectType.method,
        accordionTitles: {
            geoTextile: 'geotextile_selection_question', // Titolo personalizzato per l'accordion geotextile
            geoGrid: 'select_geogrid',
        },
        fields: {
            projectSettings: [
                {
                    name: 'calculationMethod',
                    type: 'string',
                    renderAs: 'select',
                    label: 'calculation_method',
                    required: true,
                    defaultValue: 'leng',
                    options: [
                        { value: 'giroud', label: 'Giroud-Han' },
                        { value: 'leng', label: 'Leng-Gabr' },
                    ],
                    isConvertible: true,
                },
            ],
            load: [
                {
                    name: 'designAxleLoad',
                    type: 'number',
                    label: 'design_axle_load',
                    required: true,
                    defaultValue: 150,
                    unit: conversionLabel.kN_lbf,
                    min: 0,
                    max: 1000,
                    symbol: 'W',
                    isConvertible: true,
                },
                {
                    name: 'tyreInflationPressure',
                    type: 'number',
                    label: 'tyre_inflation_pressure',
                    required: true,
                    defaultValue: 700,
                    unit: conversionLabel.kPa,
                    min: 0,
                    max: 1000,
                    symbol: 'p',
                    isConvertible: true,
                },
                {
                    name: 'esal',
                    type: 'number',
                    label: 'equivalent_standard_axle_load',
                    required: true,
                    defaultValue: 80,
                    unit: conversionLabel.kN_lbf,
                    min: 0,
                    max: 1000,
                    symbol: 'Pa',
                    isConvertible: true,
                },
                {
                    name: 'numPassages',
                    type: 'number',
                    label: 'number_of_passages_of_design_axle_load',
                    required: true,
                    defaultValue: 640000,
                    unit: '',
                    min: 0,
                    max: 1000000000,
                    symbol: 'Np',
                    isConvertible: false,
                },
                { name: 'allowableRutDepth', type: 'number', label: 'Allowable rut depth', required: true, defaultValue: 50, unit: conversionLabel.mm_in, symbol: 's', isConvertible: true, min: 0, max: 500 },
            ],
            subgrade: [
                {
                    name: 'subgradeInputMethod',
                    label: 'subgrade_input_method_label',
                    type: 'string',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'cbr', label: 'subgrade_option_cbr' },
                        { value: 'modulus', label: 'subgrade_option_modulus' },
                    ],
                    defaultValue: 'cbr',
                    required: true,
                },
                // Base Course fields - CBR mode
                {
                    name: 'cbrBaseCourse',
                    type: 'number',
                    label: 'cbr_base_course_label', // "CBR of base course"
                    required: false,
                    defaultValue: 15.0,
                    unit: ['%'],
                    isConvertible: false,
                    min: 0,
                    max: 100,
                    symbol: 'CBRbc',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                {
                    name: 'cbrSubgrade',
                    type: 'number',
                    label: 'cbr_subgrade_label', // "CBR of subgrade"
                    required: false,
                    defaultValue: 2.0,
                    unit: ['%'],
                    isConvertible: false,
                    min: 0,
                    max: 3,
                    symbol: 'CBRsg ≤ 3',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                // Base Course fields - Ev2 mode
                {
                    name: 'ev2BaseCourse',
                    type: 'number',
                    label: 'ev2_base_course_label', // "Ev2 of base course"
                    required: false,
                    defaultValue: 100.0,
                    unit: ['MPa'],
                    isConvertible: false,
                    min: 0,
                    max: 1000,
                    symbol: 'Ev2bc',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'modulus',
                    },
                },
                {
                    name: 'ev2Subgrade',
                    type: 'number',
                    label: 'ev2_subgrade_label', // "Ev2 of subgrade"
                    required: false,
                    defaultValue: 40.0,
                    unit: ['MPa'],
                    isConvertible: false,
                    min: 0,
                    max: 500,
                    symbol: 'Ev2sg ≤ 3',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'modulus',
                    },
                },
            ],
            geoGrid: [
                {
                    name: 'selectedGeogrid',
                    type: 'string',
                    label: 'selected_geogrid_product',
                    renderAs: 'select',
                    // TODO: Phase 3/4 - Use ConfigurationService.getGeogridDefaults().unpaved
                    // Source of Truth: pavement-database.json designParameters.geogridDefaults.unpaved
                    defaultValue: 'MACGRID WG 3S',
                    required: false,
                    optionsSource: 'unpavedGiroudData',
                },
            ],
            geoTextile: [
                {
                    name: 'selectedGeotextile',
                    type: 'string',
                    label: 'selected_geotextile_product',
                    renderAs: 'select',
                    defaultValue: '',
                    required: false,
                    optionsSource: 'unpavedGiroudGeotextiles',
                },
            ],
        },
    };
};

const getRailwayDefault = (projectType: { type: string; method: string }): ProjectTypeJson => {
    return {
        projectType: projectType.type,
        projectMethod: projectType.method,
        accordionTitles: {
            geoGrid: 'geogrid_selection_question',
            railwayType: 'railway_type',
            loadData: 'load_data',
            trackGeometry: 'track_geometry',
            trafficData: 'traffic_data',
            soilProperties: 'soil_properties',
            poissonRatios: 'poisson_ratios',
            materialTypes: 'material_types',
            designParameters: 'design_parameters',
        },
        fields: {
            railwayType: [
                {
                    name: 'railwayToDesign',
                    type: 'string',
                    label: 'railway_to_design',
                    renderAs: 'select',
                    options: [
                        { value: 'cargo_train', label: 'cargo_train' },
                        { value: 'passenger_train', label: 'passenger_train' },
                        { value: 'mixed_train', label: 'mixed_train' },
                    ],
                    defaultValue: 'cargo_train',
                    required: true,
                },
            ],
            loadData: [
                // Original fields for cargo_train only
                {
                    name: 'designAxleLoad',
                    type: 'number',
                    label: 'design_axle_load',
                    required: true,
                    defaultValue: 500,
                    unit: conversionLabel.kN_lbf,
                    min: 0,
                    max: 1000,
                    symbol: 'W',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'cargo_train',
                    },
                },
                {
                    name: 'trainSpeed',
                    type: 'number',
                    label: 'train_speed',
                    required: true,
                    defaultValue: 60,
                    unit: conversionLabel.kmh_mph,
                    min: 0,
                    max: 500,
                    symbol: 'C',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'cargo_train',
                    },
                },
                {
                    name: 'wheelDiameter',
                    type: 'number',
                    label: 'wheel_diameter',
                    required: true,
                    defaultValue: 0.915,
                    unit: conversionLabel.m_in,
                    min: 0,
                    max: 2,
                    symbol: 'Dw',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'cargo_train',
                    },
                },
                // Original fields for passenger_train only
                {
                    name: 'designAxleLoad',
                    type: 'number',
                    label: 'design_axle_load',
                    required: true,
                    defaultValue: 170,
                    unit: conversionLabel.kN_lbf,
                    min: 0,
                    max: 1000,
                    symbol: 'W',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'passenger_train',
                    },
                },
                {
                    name: 'trainSpeed',
                    type: 'number',
                    label: 'train_speed',
                    required: true,
                    defaultValue: 300,
                    unit: conversionLabel.kmh_mph,
                    min: 0,
                    max: 500,
                    symbol: 'C',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'passenger_train',
                    },
                },
                {
                    name: 'wheelDiameter',
                    type: 'number',
                    label: 'wheel_diameter',
                    required: true,
                    defaultValue: 0.9,
                    unit: conversionLabel.m_in,
                    min: 0,
                    max: 2,
                    symbol: 'Dw',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'passenger_train',
                    },
                },
                // Cargo train fields - shown only when mixed_train is selected
                {
                    name: 'cargoTrainHeader',
                    type: 'string',
                    label: 'cargo_train_parameters',
                    required: false,
                    defaultValue: '',
                    renderAs: 'custom',
                    isReadOnly: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'designAxleLoadCargo',
                    type: 'number',
                    label: 'design_axle_load_cargo',
                    required: true,
                    defaultValue: 500,
                    unit: conversionLabel.kN_lbf,
                    min: 0,
                    max: 1000,
                    symbol: 'W',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'trainSpeedCargo',
                    type: 'number',
                    label: 'train_speed_cargo',
                    required: true,
                    defaultValue: 60,
                    unit: conversionLabel.kmh_mph,
                    min: 0,
                    max: 500,
                    symbol: 'C',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'wheelDiameterCargo',
                    type: 'number',
                    label: 'wheel_diameter_cargo',
                    required: true,
                    defaultValue: 0.915,
                    unit: conversionLabel.m_in,
                    min: 0,
                    max: 2,
                    symbol: 'Dw',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                // Passenger train fields - shown only when mixed_train is selected
                {
                    name: 'passengerTrainHeader',
                    type: 'string',
                    label: 'passenger_train_parameters',
                    required: false,
                    defaultValue: '',
                    renderAs: 'custom',
                    isReadOnly: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'designAxleLoadPassenger',
                    type: 'number',
                    label: 'design_axle_load_passenger',
                    required: true,
                    defaultValue: 170,
                    unit: conversionLabel.kN_lbf,
                    min: 0,
                    max: 1000,
                    symbol: 'W',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'trainSpeedPassenger',
                    type: 'number',
                    label: 'train_speed_passenger',
                    required: true,
                    defaultValue: 300,
                    unit: conversionLabel.kmh_mph,
                    min: 0,
                    max: 500,
                    symbol: 'C',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'wheelDiameterPassenger',
                    type: 'number',
                    label: 'wheel_diameter_passenger',
                    required: true,
                    defaultValue: 0.9,
                    unit: conversionLabel.m_in,
                    min: 0,
                    max: 2,
                    symbol: 'Dw',
                    isConvertible: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
            ],
            trackGeometry: [
                {
                    name: 'railWidth',
                    type: 'number',
                    label: 'rail_width',
                    required: true,
                    defaultValue: 0.16,
                    unit: conversionLabel.m_in,
                    min: 0,
                    max: 1,
                    symbol: 'br',
                    isConvertible: true,
                },
                {
                    name: 'sleeperThickness',
                    type: 'number',
                    label: 'sleeper_thickness',
                    required: true,
                    defaultValue: 0.221,
                    unit: conversionLabel.m_in,
                    min: 0,
                    max: 1,
                    symbol: 'ts',
                    isConvertible: true,
                },
                {
                    name: 'sleeperWidth',
                    type: 'number',
                    label: 'sleeper_width',
                    required: true,
                    defaultValue: 0.25,
                    unit: conversionLabel.m_in,
                    min: 0,
                    max: 2,
                    symbol: 'bs',
                    isConvertible: true,
                },
                {
                    name: 'loadSpreadingAngle',
                    type: 'number',
                    label: 'load_spreading_angle_in_sleepers',
                    required: true,
                    defaultValue: 60,
                    unit: 'deg',
                    min: 0,
                    max: 90,
                    symbol: 'αs',
                    isConvertible: false,
                },
            ],
            trafficData: [
                // Unified fields for cargo_train and passenger_train, hidden for mixed_train
                {
                    name: 'numberOfTrainsPerDay',
                    type: 'number',
                    label: 'number_of_trains_per_day',
                    required: true,
                    defaultValue: 50, // cargo_train default
                    unit: '-',
                    min: 0,
                    max: 1000,
                    symbol: 'Nt',
                    isConvertible: false,
                    hideWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'numberOfCarsPerTrain',
                    type: 'number',
                    label: 'number_of_cars_per_train',
                    required: true,
                    defaultValue: 10,
                    unit: '-',
                    min: 0,
                    max: 100,
                    symbol: 'Nw',
                    isConvertible: false,
                    hideWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'numberOfAxisPerCar',
                    type: 'number',
                    label: 'number_of_axis_per_car',
                    required: true,
                    defaultValue: 4,
                    unit: '-',
                    min: 0,
                    max: 20,
                    symbol: 'Np',
                    isConvertible: false,
                    hideWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'designLife',
                    type: 'number',
                    label: 'design_life',
                    required: true,
                    defaultValue: 20,
                    unit: 'years',
                    min: 0,
                    max: 100,
                    symbol: 'T',
                    isConvertible: false,
                    hideWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                // Cargo train fields - shown only when mixed_train is selected
                {
                    name: 'cargoTrainTrafficHeader',
                    type: 'string',
                    label: 'cargo_train_traffic_data',
                    required: false,
                    defaultValue: '',
                    renderAs: 'custom',
                    isReadOnly: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'numberOfTrainsPerDayCargo',
                    type: 'number',
                    label: 'number_of_trains_per_day_cargo',
                    required: true,
                    defaultValue: 50,
                    unit: '-',
                    min: 0,
                    max: 1000,
                    symbol: 'Nt',
                    isConvertible: false,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'numberOfCarsPerTrainCargo',
                    type: 'number',
                    label: 'number_of_cars_per_train_cargo',
                    required: true,
                    defaultValue: 10,
                    unit: '-',
                    min: 0,
                    max: 100,
                    symbol: 'Nw',
                    isConvertible: false,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'numberOfAxisPerCarCargo',
                    type: 'number',
                    label: 'number_of_axis_per_car_cargo',
                    required: true,
                    defaultValue: 4,
                    unit: '-',
                    min: 0,
                    max: 20,
                    symbol: 'Np',
                    isConvertible: false,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'designLifeCargo',
                    type: 'number',
                    label: 'design_life_cargo',
                    required: true,
                    defaultValue: 20,
                    unit: 'years',
                    min: 0,
                    max: 100,
                    symbol: 'T',
                    isConvertible: false,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                // Passenger train fields - shown only when mixed_train is selected
                {
                    name: 'passengerTrainTrafficHeader',
                    type: 'string',
                    label: 'passenger_train_traffic_data',
                    required: false,
                    defaultValue: '',
                    renderAs: 'custom',
                    isReadOnly: true,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'numberOfTrainsPerDayPassenger',
                    type: 'number',
                    label: 'number_of_trains_per_day_passenger',
                    required: true,
                    defaultValue: 1,
                    unit: '-',
                    min: 0,
                    max: 1000,
                    symbol: 'Nt',
                    isConvertible: false,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'numberOfCarsPerTrainPassenger',
                    type: 'number',
                    label: 'number_of_cars_per_train_passenger',
                    required: true,
                    defaultValue: 10,
                    unit: '-',
                    min: 0,
                    max: 100,
                    symbol: 'Nw',
                    isConvertible: false,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'numberOfAxisPerCarPassenger',
                    type: 'number',
                    label: 'number_of_axis_per_car_passenger',
                    required: true,
                    defaultValue: 4,
                    unit: '-',
                    min: 0,
                    max: 20,
                    symbol: 'Np',
                    isConvertible: false,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
                {
                    name: 'designLifePassenger',
                    type: 'number',
                    label: 'design_life_passenger',
                    required: true,
                    defaultValue: 20,
                    unit: 'years',
                    min: 0,
                    max: 100,
                    symbol: 'T',
                    isConvertible: false,
                    showWhen: {
                        field: 'railwayToDesign',
                        value: 'mixed_train',
                    },
                },
            ],
            soilProperties: [
                {
                    name: 'thicknessOfSoftSubgrade',
                    type: 'number',
                    label: 'thickness_of_soft_subgrade',
                    required: true,
                    defaultValue: 5,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 50,
                    symbol: 'Hs',
                    isConvertible: true,
                },
                {
                    name: 'cbrOfBallast',
                    type: 'number',
                    label: 'cbr_of_ballast',
                    required: true,
                    defaultValue: 100, // Default to granite/basalt standard
                    unit: '-',
                    min: 0,
                    max: 100,
                    symbol: 'CBRb',
                    isConvertible: false,
                    isReadOnly: true, // Locked field - auto-calculated from ballast type
                },
                {
                    name: 'cbrOfSubballast',
                    type: 'number',
                    label: 'cbr_of_subballast',
                    required: true,
                    defaultValue: 30.0,
                    unit: '-',
                    min: 0,
                    max: 100,
                    symbol: 'CBRsb',
                    isConvertible: false,
                },
                {
                    name: 'cbrOfSubgrade',
                    type: 'number',
                    label: 'cbr_of_subgrade',
                    required: true,
                    defaultValue: 5.0,
                    unit: '-',
                    min: 0,
                    max: 100,
                    symbol: 'CBRsg',
                    isConvertible: false,
                },
            ],
            poissonRatios: [
                {
                    name: 'poissonRatioOfBallast',
                    type: 'number',
                    label: 'poisson_ratio_of_ballast',
                    required: true,
                    defaultValue: 0.35,
                    unit: '-',
                    min: 0,
                    max: 0.5,
                    symbol: 'μb',
                    isConvertible: false,
                },
                {
                    name: 'poissonRatioOfSubballast',
                    type: 'number',
                    label: 'poisson_ratio_of_subballast',
                    required: true,
                    defaultValue: 0.42,
                    unit: '-',
                    min: 0,
                    max: 0.5,
                    symbol: 'μsb',
                    isConvertible: false,
                },
                {
                    name: 'poissonRatioOfSubgrade',
                    type: 'number',
                    label: 'poisson_ratio_of_subgrade',
                    required: true,
                    defaultValue: 0.3,
                    unit: '-',
                    min: 0,
                    max: 0.5,
                    symbol: 'μsg',
                    isConvertible: false,
                },
            ],
            materialTypes: [
                {
                    name: 'ballastType',
                    type: 'string',
                    label: 'ballast_type',
                    renderAs: 'select',
                    options: getBallastOptions(),
                    defaultValue: 'granite',
                    required: true,
                },
                {
                    name: 'subgradeSoilType',
                    type: 'string',
                    label: 'subgrade_soil_type',
                    renderAs: 'select',
                    options: SUBGRADE_SOIL_TYPE_OPTIONS,
                    defaultValue: 'lean_clay',
                    required: true,
                },
            ],
            geoGrid: [
                {
                    name: 'selectedGeogrid',
                    type: 'string',
                    label: 'selected_geogrid_product',
                    renderAs: 'select',
                    // TODO: Phase 3/4 - Use ConfigurationService.getGeogridDefaults().railway
                    // Source of Truth: pavement-database.json designParameters.geogridDefaults.railway
                    defaultValue: 'MACGRID EG 20S',
                    required: true,
                    optionsSource: 'railwayGeogridsData',
                },
                {
                    name: 'tensileStrengthAt2Percent',
                    type: 'number',
                    label: 'tensile_strength_at_2_percent_strain',
                    required: false,
                    defaultValue: 7.0,
                    unit: conversionLabel.kNm_lbfft,
                    min: 0,
                    max: 100,
                    symbol: '',
                    isConvertible: true,
                    isReadOnly: true,
                    calculation: calculateTensileStrengthAt2Percent,
                },
            ],
            designParameters: [
                {
                    name: 'setStabilisedBallastThickness',
                    type: 'number',
                    label: 'set_stabilised_ballast_thickness_with_subballast_below',
                    required: true,
                    defaultValue: 0.2,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    symbol: 'h"b',
                    isConvertible: true,
                },
            ],
        },
    };
};

const getAirportDefault = (projectType: { type: string; method: string }): ProjectTypeJson => {
    return {
        projectType: projectType.type,
        projectMethod: projectType.method,
        accordionTitles: {
            roadInfo: 'runway_info', // Custom title for Airport projects
        },
        fields: {
            roadInfo: [
                {
                    name: 'carriagewayWidth',
                    type: 'number',
                    label: 'runway_width',
                    required: true,
                    defaultValue: 45,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 100,
                    symbol: 'W',
                    isConvertible: true,
                },
                {
                    name: 'longitudinalSlope',
                    type: 'number',
                    label: 'longitudinal_slope',
                    required: true,
                    defaultValue: 1,
                    unit: '%',
                    min: 0,
                    max: 10,
                    symbol: 'S',
                    isConvertible: true,
                },
                {
                    name: 'transversalSlope',
                    type: 'number',
                    label: 'transversal_slope',
                    required: true,
                    defaultValue: 1,
                    unit: '%',
                    min: 0,
                    max: 10,
                    symbol: 'Sx',
                    isConvertible: true,
                },
                {
                    name: 'subgradeInputMethod',
                    label: 'subgrade_cbr_ev2_choice',
                    type: 'string',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'cbr', label: 'CBR' },
                        { value: 'ev2', label: 'Ev2' },
                    ],
                    defaultValue: 'cbr',
                    required: true,
                },
            ],
            trafficLoad: [
                {
                    name: 'functionalClassification',
                    type: 'string',
                    label: 'aircraft_classification',
                    renderAs: 'select',
                    required: true,
                    defaultValue: 'Commercial Heavy',
                    options: [
                        { value: 'Commercial Heavy', label: 'Commercial Heavy' },
                        { value: 'Commercial Medium', label: 'Commercial Medium' },
                        { value: 'Commercial Light', label: 'Commercial Light' },
                        { value: 'General Aviation', label: 'General Aviation' },
                    ],
                },
                {
                    name: 'roadType',
                    type: 'string',
                    label: 'airport_type',
                    renderAs: 'select',
                    required: true,
                    defaultValue: 'International',
                    options: [
                        { value: 'International', label: 'International' },
                        { value: 'Regional', label: 'Regional' },
                        { value: 'Domestic', label: 'Domestic' },
                    ],
                },
                {
                    name: 'reliabilityLevel',
                    type: 'number',
                    label: 'reliability_level_r',
                    required: true,
                    defaultValue: 95,
                    unit: '%',
                    min: 0,
                    max: 100,
                    symbol: 'R',
                    layout: {
                        inline: true,
                        width: '60%',
                        position: 'left',
                        companion: 'reliabilityLevelSuggested',
                    },
                },
                {
                    name: 'reliabilityLevelSuggested',
                    type: 'string',
                    label: 'reliability_level_suggested',
                    required: false,
                    defaultValue: '90-99',
                    isReadOnly: true,
                    calculation: calculateReliabilityLevelSuggested,
                    layout: {
                        inline: true,
                        width: '40%',
                        position: 'right',
                        companion: 'reliabilityLevel',
                    },
                },
                {
                    name: 'combinedStandardError',
                    type: 'number',
                    label: 'combined_standard_error_so',
                    required: true,
                    defaultValue: 0.5,
                    min: 0,
                    max: 10,
                    symbol: 'So',
                },
                {
                    name: 'initialServiceIndex',
                    type: 'number',
                    label: 'initial_service_index_po',
                    required: true,
                    defaultValue: 4.5,
                    min: 0,
                    max: 5,
                    symbol: 'Po',
                },
                {
                    name: 'terminalSurfaceIndex',
                    type: 'number',
                    label: 'terminal_surface_index_pt',
                    required: true,
                    defaultValue: 2.5,
                    min: 0,
                    max: 5,
                    symbol: 'Pt',
                },
                {
                    name: 'esalCalculationMode',
                    type: 'string',
                    label: 'esal_calculation_mode_aircraft',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'automatic', label: 'Automatic' },
                        { value: 'manual', label: 'Manual' },
                    ],
                    defaultValue: 'manual',
                    required: true,
                },
                {
                    name: 'esalFinalValue',
                    type: 'number',
                    label: 'esal_final_value_aircraft',
                    required: false,
                    defaultValue: 111111,
                    min: 0,
                    max: 1e10,
                    symbol: 'ESAL',
                    format: 'integer',
                },
            ],
            drainageDesign: [
                {
                    name: 'drainageBelowBase',
                    type: 'string',
                    label: 'drainage_below_base',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: true,
                    mutuallyExclusive: 'drainageBelowSubbase',
                },
                {
                    name: 'drainageBelowSubbase',
                    type: 'string',
                    label: 'drainage_below_subbase',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: true,
                    mutuallyExclusive: 'drainageBelowBase',
                },
                {
                    name: 'baseDrainageType',
                    type: 'string',
                    label: 'select_drainage_for_base',
                    renderAs: 'select',
                    options: materialDatabaseService.getBaseDrainageOptions(),
                    defaultValue: materialDatabaseService.getDefaultBaseDrainage().id,
                    required: false,
                    showWhen: {
                        field: 'drainageBelowBase',
                        value: 'yes',
                    },
                },
                {
                    name: 'subbaseDrainageType',
                    type: 'string',
                    label: 'select_drainage_for_subbase',
                    renderAs: 'select',
                    options: materialDatabaseService.getSubbaseDrainageOptions(),
                    defaultValue: materialDatabaseService.getDefaultSubbaseDrainage().id,
                    required: false,
                    showWhen: {
                        field: 'drainageBelowSubbase',
                        value: 'yes',
                    },
                },
                {
                    name: 'referenceTimeForDrainage',
                    type: 'string',
                    label: 'reference_time_for_drainage',
                    renderAs: 'select',
                    options: [
                        { value: 'hours', label: 'Hours' },
                        { value: 'days', label: 'Days' },
                        { value: 'weeks', label: 'Weeks' },
                        { value: 'months', label: 'Months' },
                    ],
                    defaultValue: 'hours',
                    required: true,
                },
                {
                    name: 'pavementMoistureExposure',
                    type: 'string',
                    label: 'percentage_pavement_exposed_moisture',
                    renderAs: 'select',
                    options: [
                        { value: '2_25', label: '2 - 25 %' },
                        { value: '25_50', label: '25 - 50 %' },
                        { value: '50_75', label: '50 - 75 %' },
                        { value: '75_100', label: '75 - 100 %' },
                    ],
                    defaultValue: '2_25',
                    required: true,
                },
            ],
            surface: [
                {
                    name: 'surfaceMaterial',
                    type: 'string',
                    label: 'select_surface_material',
                    renderAs: 'select',
                    options: materialDatabaseService.getSurfaceMaterialOptions(),
                    defaultValue: materialDatabaseService.getDefaultSurfaceMaterial().id,
                    required: true,
                },
                {
                    name: 'minThickness',
                    type: 'number',
                    label: 'minimum_thickness_of_surface_layer',
                    required: true,
                    defaultValue: 0.05,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'left',
                        companion: 'minThicknessSuggested',
                    },
                },
                {
                    name: 'minThicknessSuggested',
                    type: 'number',
                    label: 'minimum_thickness_suggested_surface',
                    required: false,
                    defaultValue: 0.1,
                    unit: conversionLabel.m_ft,
                    isReadOnly: true,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'right',
                        companion: 'minThickness',
                    },
                },
                {
                    name: 'layerCoefficienta1',
                    type: 'number',
                    label: 'layer_coefficient',
                    required: false,
                    defaultValue: (() => {
                        const defaultMaterial = materialDatabaseService.getDefaultSurfaceMaterial();
                        return getLayerCoefficientForSurfaceMaterial(defaultMaterial.id);
                    })(),
                    isReadOnly: true,
                    calculation: calculateSurfaceLayerCoefficient,
                },
            ],
            baseCourse: [
                {
                    name: 'baseMaterial',
                    type: 'string',
                    label: 'select_base_material',
                    renderAs: 'select',
                    options: materialDatabaseService.getBaseMaterialOptions(),
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().id,
                    required: true,
                    infoBoxTableData: {
                        headers: ['Material', 'Marshal Stability', 'CBR', 'Ev2', 'Layer Coefficient', 'N0', 'k'],
                        rows: [
                            ['Crushed gravel 1#', '-', '100', '335.4', '0.14', '0.21', '5.00E-03'],
                            ['Crushed gravel 2#', '-', '90', '313.5', '0.13', '0.32', '5.00E-02'],
                            ['Mixed granular soil 1#', '-', '70', '266.9', '0.12', '0.32', '1.00E-04'],
                            ['Mixed granular soil 2#', '-', '50', '215.2', '0.10', '0.25', '5.00E-04'],
                        ],
                        caption: 'BASE Material Properties and Coefficients',
                    },
                },
                {
                    name: 'minThickness',
                    type: 'number',
                    label: 'minimum_thickness_of_base_layer',
                    required: true,
                    defaultValue: 0.15,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'left',
                        companion: 'minThicknessSuggested',
                    },
                },
                {
                    name: 'minThicknessSuggested',
                    type: 'number',
                    label: 'minimum_thickness_suggested_base',
                    required: false,
                    defaultValue: 0.15,
                    unit: conversionLabel.m_ft,
                    isReadOnly: true,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'right',
                        companion: 'minThickness',
                    },
                },
                {
                    name: 'layerCoefficienta2',
                    type: 'number',
                    label: 'layer_coefficient',
                    required: false,
                    defaultValue: (() => {
                        const defaultMaterial = materialDatabaseService.getDefaultBaseMaterial();
                        return getLayerCoefficientForBaseMaterial(defaultMaterial.id);
                    })(),
                    isReadOnly: true,
                    calculation: calculateBaseLayerCoefficient,
                },
                {
                    name: 'baseGeogrid',
                    type: 'string',
                    label: 'select_geogrid_for_base_course',
                    renderAs: 'select',
                    options: materialDatabaseService.getBaseGeogridOptions(),
                    defaultValue: '', // No default geogrid selection (user must explicitly choose)
                    required: false,
                },
                {
                    name: 'effectivePorosity',
                    type: 'number',
                    label: 'effective_porosity_of_base_course',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().effectivePorosity,
                    isReadOnly: true,
                },
                {
                    name: 'permeabilityCoefficient',
                    type: 'number',
                    label: 'permeability_coefficient_of_base_course',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().permeabilityCoefficient,
                    isReadOnly: true,
                },
                {
                    name: 'cbrBaseCourse',
                    type: 'number',
                    label: 'cbr_base_course_label',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().cbr,
                    isReadOnly: true,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                {
                    name: 'ev2BaseCourse',
                    type: 'number',
                    label: 'ev2_base_course_label',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().ev2,
                    isReadOnly: true,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'ev2',
                    },
                },
            ],
            subbaseCourse: [
                {
                    name: 'subbaseMaterial',
                    type: 'string',
                    label: 'select_subbase_material',
                    renderAs: 'select',
                    options: materialDatabaseService.getSubbaseMaterialOptions(),
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().id,
                    required: true,
                    infoBoxTableData: {
                        headers: ['Material', 'Marshal Stability', 'CBR', 'Ev2', 'Layer Coefficient', 'N0', 'k'],
                        rows: [
                            ['Crushed gravel', '-', '90', '313.5', '0.14', '0.32', '5.00E-02'],
                            ['Mixed granular soil 1#', '-', '70', '266.9', '0.13', '0.32', '1.00E-04'],
                            ['Mixed granular soil 2#', '-', '50', '215.2', '0.12', '0.25', '5.00E-05'],
                            ['Mixed granular soil 3#', '-', '30', '155.2', '0.11', '0.3', '1.00E-05'],
                            ['Naturally stabilized mix 1#', '-', '20', '119.7', '0.1', '0.25', '5.00E-06'],
                            ['Naturally stabilized mix 2#', '-', '10', '76.8', '0.075', '0.2', '1.00E-06'],
                            ['Naturally stabilized mix 3#', '-', '5', '50.0', '0.05', '0.15', '5.00E-07'],
                        ],
                        caption: 'SUBBASE Material Properties and Coefficients',
                    },
                },
                {
                    name: 'minThickness',
                    type: 'number',
                    label: 'minimum_thickness_of_subbase_layer',
                    required: true,
                    defaultValue: 0.15,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'left',
                        companion: 'minThicknessSuggested',
                    },
                },
                {
                    name: 'minThicknessSuggested',
                    type: 'number',
                    label: 'minimum_thickness_suggested_subbase',
                    required: false,
                    defaultValue: 0.15,
                    unit: conversionLabel.m_ft,
                    isReadOnly: true,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'right',
                        companion: 'minThickness',
                    },
                },
                {
                    name: 'layerCoefficienta3',
                    type: 'number',
                    label: 'layer_coefficient',
                    required: false,
                    defaultValue: (() => {
                        const defaultMaterial = materialDatabaseService.getDefaultSubbaseMaterial();
                        return getLayerCoefficientForSubbaseMaterial(defaultMaterial.id);
                    })(),
                    isReadOnly: true,
                    calculation: calculateSubbaseLayerCoefficient,
                },
                {
                    name: 'subbaseGeogrid',
                    type: 'string',
                    label: 'select_geogrid_for_subbase_course',
                    renderAs: 'select',
                    options: materialDatabaseService.getSubbaseGeogridOptions(),
                    defaultValue: '', // No default geogrid selection (user must explicitly choose)
                    required: false,
                },
                {
                    name: 'effectivePorosity',
                    type: 'number',
                    label: 'effective_porosity_of_subbase_course',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().effectivePorosity,
                    isReadOnly: true,
                },
                {
                    name: 'permeabilityCoefficient',
                    type: 'number',
                    label: 'permeability_coefficient_of_subbase_course',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().permeabilityCoefficient,
                    isReadOnly: true,
                },
                {
                    name: 'cbrSubbaseCourse',
                    type: 'number',
                    label: 'cbr_subbase_course_label',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().cbr,
                    isReadOnly: true,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                {
                    name: 'ev2SubbaseCourse',
                    type: 'number',
                    label: 'ev2_subbase_course_label',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().ev2,
                    isReadOnly: true,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'ev2',
                    },
                },
            ],
            subgrade: [
                {
                    name: 'cbrSubgrade',
                    type: 'number',
                    label: 'cbr_subgrade_label',
                    required: false,
                    defaultValue: 2,
                    min: 0,
                    max: 100,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                {
                    name: 'ev2Subgrade',
                    type: 'number',
                    label: 'ev2_subgrade_label',
                    required: false,
                    defaultValue: 50,
                    min: 0,
                    max: 500,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'ev2',
                    },
                },
            ],
            surfaceDesign: [
                {
                    name: 'enableSurfaceDesign',
                    type: 'string',
                    label: 'enable_surface_design_analysis',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: false,
                    mutuallyExclusiveGroup: ['enableSurfaceDesign', 'enableBaseDesign', 'enableSubbaseDesign'],
                },
                {
                    name: 'baseThickness',
                    type: 'number',
                    label: 'base_thickness_for_surface_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.2,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₂',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'baseCourse',
                    },
                    showWhen: {
                        field: 'enableSurfaceDesign',
                        value: 'yes',
                    },
                },
                {
                    name: 'subbaseThickness',
                    type: 'number',
                    label: 'subbase_thickness_for_surface_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.4,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₃',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'subbaseCourse',
                    },
                    showWhen: {
                        field: 'enableSurfaceDesign',
                        value: 'yes',
                    },
                },
            ],
            baseDesign: [
                {
                    name: 'enableBaseDesign',
                    type: 'string',
                    label: 'enable_base_design_analysis',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: false,
                    mutuallyExclusiveGroup: ['enableSurfaceDesign', 'enableBaseDesign', 'enableSubbaseDesign'],
                },
                {
                    name: 'surfaceThickness',
                    type: 'number',
                    label: 'surface_thickness_for_base_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.1,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₁',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'surface',
                    },
                    showWhen: {
                        field: 'enableBaseDesign',
                        value: 'yes',
                    },
                },
                {
                    name: 'subbaseThickness',
                    type: 'number',
                    label: 'subbase_thickness_for_base_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.4,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₃',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'subbaseCourse',
                    },
                    showWhen: {
                        field: 'enableBaseDesign',
                        value: 'yes',
                    },
                },
            ],
            subbaseDesign: [
                {
                    name: 'enableSubbaseDesign',
                    type: 'string',
                    label: 'enable_subbase_design_analysis',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: false,
                    mutuallyExclusiveGroup: ['enableSurfaceDesign', 'enableBaseDesign', 'enableSubbaseDesign'],
                },
                {
                    name: 'surfaceThickness',
                    type: 'number',
                    label: 'surface_thickness_for_subbase_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.1,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₁',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'surface',
                    },
                    showWhen: {
                        field: 'enableSubbaseDesign',
                        value: 'yes',
                    },
                },
                {
                    name: 'baseThickness',
                    type: 'number',
                    label: 'base_thickness_for_subbase_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.2,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₂',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'baseCourse',
                    },
                    showWhen: {
                        field: 'enableSubbaseDesign',
                        value: 'yes',
                    },
                },
            ],
        },
    };
};

const getPavedAsphalt = (projectType: { type: string; method: string }): ProjectTypeJson => {
    return {
        projectType: projectType.type,
        projectMethod: projectType.method,
        accordionTitles: {
            roadInfo: 'road_geometry',
            trafficLoad: 'traffic_data',
            drainageDesign: 'drainage_design',
            surface: 'surface_layer',
            baseCourse: 'base',
            subbaseCourse: 'subbase',
            surfaceDesign: 'surface_design_analysis',
            baseDesign: 'base_design_analysis',
            subbaseDesign: 'subbase_design_analysis',
        },
        fields: {
            roadInfo: [
                {
                    name: 'carriagewayWidth',
                    type: 'number',
                    label: 'width_of_carriageway_one_direction',
                    required: true,
                    defaultValue: 3.5,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 20,
                    symbol: 'W',
                    isConvertible: true,
                },
                {
                    name: 'longitudinalSlope',
                    type: 'number',
                    label: 'longitudinal_slope',
                    required: true,
                    defaultValue: 2,
                    unit: '%',
                    min: 0,
                    max: 20,
                    symbol: 'S',
                    isConvertible: true,
                },
                {
                    name: 'transversalSlope',
                    type: 'number',
                    label: 'transversal_slope',
                    required: true,
                    defaultValue: 2,
                    unit: '%',
                    min: 0,
                    max: 20,
                    symbol: 'Sx',
                    isConvertible: true,
                },
                {
                    name: 'subgradeInputMethod',
                    label: 'subgrade_cbr_ev2_choice',
                    type: 'string',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'cbr', label: 'CBR' },
                        { value: 'ev2', label: 'Ev2' },
                    ],
                    defaultValue: 'cbr',
                    required: true,
                },
            ],
            trafficLoad: [
                {
                    name: 'functionalClassification',
                    type: 'string',
                    label: 'functional_classification',
                    renderAs: 'select',
                    required: true,
                    defaultValue: 'Interstate and Other Freeways',
                    options: [
                        { value: 'Interstate and Other Freeways', label: 'Interstate and Other Freeways' },
                        { value: 'Principial Arterials', label: 'Principial Arterials' },
                        { value: 'Collector', label: 'Collector' },
                        { value: 'Local', label: 'Local' },
                    ],
                },
                {
                    name: 'roadType',
                    type: 'string',
                    label: 'road_type',
                    renderAs: 'select',
                    required: true,
                    defaultValue: 'Urban',
                    options: [
                        { value: 'Urban', label: 'Urban' },
                        { value: 'Rural', label: 'Rural' },
                    ],
                },
                {
                    name: 'reliabilityLevel',
                    type: 'number',
                    label: 'reliability_level_r',
                    required: true,
                    defaultValue: 95,
                    unit: '%',
                    min: 0,
                    max: 100,
                    symbol: 'R',
                    layout: {
                        inline: true,
                        width: '60%',
                        position: 'left',
                        companion: 'reliabilityLevelSuggested',
                    },
                },
                {
                    name: 'reliabilityLevelSuggested',
                    type: 'string',
                    label: 'reliability_level_suggested',
                    required: false,
                    defaultValue: '80-95', // Default range as fallback
                    isReadOnly: true,
                    calculation: calculateReliabilityLevelSuggested,
                    layout: {
                        inline: true,
                        width: '40%',
                        position: 'right',
                        companion: 'reliabilityLevel',
                    },
                },
                {
                    name: 'combinedStandardError',
                    type: 'number',
                    label: 'combined_standard_error_so',
                    required: true,
                    defaultValue: 0.5,
                    min: 0,
                    max: 10,
                    symbol: 'So',
                },
                {
                    name: 'initialServiceIndex',
                    type: 'number',
                    label: 'initial_service_index_po',
                    required: true,
                    defaultValue: 4.5,
                    min: 0,
                    max: 5,
                    symbol: 'Po',
                },
                {
                    name: 'terminalSurfaceIndex',
                    type: 'number',
                    label: 'terminal_surface_index_pt',
                    required: true,
                    defaultValue: 2.5,
                    min: 0,
                    max: 5,
                    symbol: 'Pt',
                },
                {
                    name: 'esalCalculationMode',
                    type: 'string',
                    label: 'esal_calculation_mode',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'automatic', label: 'Automatic' },
                        { value: 'manual', label: 'Manual' },
                    ],
                    defaultValue: 'manual',
                    required: true,
                },
                {
                    name: 'esalFinalValue',
                    type: 'number',
                    label: 'esal_final_value',
                    required: false,
                    defaultValue: 9395483,
                    min: 0,
                    max: 1e10,
                    symbol: 'ESAL',
                    format: 'integer', // Formatta come intero senza decimali
                },
                // If esalCalculationMode is 'automatic', a popup/form will be handled in the UI (placeholder here)
            ],
            drainageDesign: [
                {
                    name: 'drainageBelowBase',
                    type: 'string',
                    label: 'drainage_below_base',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: true,
                    mutuallyExclusive: 'drainageBelowSubbase', // Cannot both be 'yes' - if this is 'yes', the other must be 'no'
                },
                {
                    name: 'drainageBelowSubbase',
                    type: 'string',
                    label: 'drainage_below_subbase',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: true,
                    mutuallyExclusive: 'drainageBelowBase', // Cannot both be 'yes' - if this is 'yes', the other must be 'no'
                },
                {
                    name: 'baseDrainageType',
                    type: 'string',
                    label: 'select_drainage_for_base',
                    renderAs: 'select',
                    options: materialDatabaseService.getBaseDrainageOptions(),
                    defaultValue: materialDatabaseService.getDefaultBaseDrainage().id,
                    required: false,
                    showWhen: {
                        field: 'drainageBelowBase',
                        value: 'yes',
                    },
                },
                {
                    name: 'subbaseDrainageType',
                    type: 'string',
                    label: 'select_drainage_for_subbase',
                    renderAs: 'select',
                    options: materialDatabaseService.getSubbaseDrainageOptions(),
                    defaultValue: materialDatabaseService.getDefaultSubbaseDrainage().id,
                    required: false,
                    showWhen: {
                        field: 'drainageBelowSubbase',
                        value: 'yes',
                    },
                },
                {
                    name: 'referenceTimeForDrainage',
                    type: 'string',
                    label: 'reference_time_for_drainage',
                    renderAs: 'select',
                    options: [
                        { value: 'hours', label: 'Hours' },
                        { value: 'days', label: 'Days' },
                        { value: 'weeks', label: 'Weeks' },
                        { value: 'months', label: 'Months' },
                    ],
                    defaultValue: 'hours',
                    required: true,
                },
                {
                    name: 'pavementMoistureExposure',
                    type: 'string',
                    label: 'percentage_pavement_exposed_moisture',
                    renderAs: 'select',
                    options: [
                        { value: '2_25', label: '2 - 25 %' },
                        { value: '25_50', label: '25 - 50 %' },
                        { value: '50_75', label: '50 - 75 %' },
                        { value: '75_100', label: '75 - 100 %' },
                    ],
                    defaultValue: '2_25',
                    required: true,
                },
            ],
            surface: [
                {
                    name: 'surfaceMaterial',
                    type: 'string',
                    label: 'select_surface_material',
                    renderAs: 'select',
                    options: materialDatabaseService.getSurfaceMaterialOptions(),
                    defaultValue: materialDatabaseService.getDefaultSurfaceMaterial().id,
                    required: true,
                    infoBoxTableData: {
                        headers: ['Material', 'Marshal Stability', 'CBR', 'Ev2', 'Layer Coefficient', 'N0', 'k'],
                        rows: [
                            ['Bituminous concrete 1#', '1000', '-', '-', '0.45', '-', '-'],
                            ['Bituminous concrete 2#', '950', '-', '-', '0.44', '-', '-'],
                            ['Bituminous concrete 3#', '770', '-', '-', '0.40', '-', '-'],
                            ['Bituminous concrete 4#', '650', '-', '-', '0.37', '-', '-'],
                            ['Bituminous concrete 5#', '410', '-', '-', '0.30', '-', '-'],
                            ['Bituminous slurry 1#', '770', '-', '-', '0.40', '-', '-'],
                            ['Bituminous slurry 2#', '580', '-', '-', '0.35', '-', '-'],
                        ],
                        caption: 'SURFACE Material Properties and Coefficients',
                    },
                },
                {
                    name: 'minThickness',
                    type: 'number',
                    label: 'minimum_thickness_of_surface_layer',
                    required: true,
                    defaultValue: 0.05,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'left',
                        companion: 'minThicknessSuggested',
                    },
                },
                {
                    name: 'minThicknessSuggested',
                    type: 'number',
                    label: 'minimum_thickness_suggested_surface',
                    required: false,
                    defaultValue: 0.1,
                    unit: conversionLabel.m_ft,
                    isReadOnly: true,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'right',
                        companion: 'minThickness',
                    },
                    // Note: This field is calculated automatically based on ESAL traffic load
                },
                {
                    name: 'layerCoefficienta1',
                    type: 'number',
                    label: 'layer_coefficient',
                    required: false,
                    defaultValue: (() => {
                        // Calculate the default value dynamically
                        const defaultMaterial = materialDatabaseService.getDefaultSurfaceMaterial();
                        return getLayerCoefficientForSurfaceMaterial(defaultMaterial.id);
                    })(),
                    isReadOnly: true,
                    calculation: calculateSurfaceLayerCoefficient,
                    // Note: This field is populated automatically based on surfaceMaterial selection
                },
            ],
            baseCourse: [
                {
                    name: 'baseMaterial',
                    type: 'string',
                    label: 'select_base_material',
                    renderAs: 'select',
                    options: materialDatabaseService.getBaseMaterialOptions(),
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().id,
                    required: true,
                    infoBoxTableData: {
                        headers: ['Material', 'Marshal Stability', 'CBR', 'Ev2', 'Layer Coefficient', 'N0', 'k'],
                        rows: [
                            ['Crushed gravel 1#', '-', '100', '335.4', '0.14', '0.21', '5.00E-03'],
                            ['Crushed gravel 2#', '-', '90', '313.5', '0.13', '0.32', '5.00E-02'],
                            ['Mixed granular soil 1#', '-', '70', '266.9', '0.12', '0.32', '1.00E-04'],
                            ['Mixed granular soil 2#', '-', '50', '215.2', '0.10', '0.25', '5.00E-04'],
                        ],
                        caption: 'BASE Material Properties and Coefficients',
                    },
                },
                {
                    name: 'minThickness',
                    type: 'number',
                    label: 'minimum_thickness_of_base_layer',
                    required: true,
                    defaultValue: 0.15,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'left',
                        companion: 'minThicknessSuggested',
                    },
                },
                {
                    name: 'minThicknessSuggested',
                    type: 'number',
                    label: 'minimum_thickness_suggested_base',
                    required: false,
                    defaultValue: 0.15,
                    unit: conversionLabel.m_ft,
                    isReadOnly: true,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'right',
                        companion: 'minThickness',
                    },
                    // Note: This field is calculated automatically based on LCR values from Design_LCR G13
                },
                {
                    name: 'layerCoefficienta2',
                    type: 'number',
                    label: 'layer_coefficient',
                    required: false,
                    defaultValue: (() => {
                        // Calculate the default value dynamically
                        const defaultMaterial = materialDatabaseService.getDefaultBaseMaterial();
                        return getLayerCoefficientForBaseMaterial(defaultMaterial.id);
                    })(),
                    isReadOnly: true,
                    calculation: calculateBaseLayerCoefficient,
                },
                {
                    name: 'baseGeogrid',
                    type: 'string',
                    label: 'select_geogrid_for_base_course',
                    renderAs: 'select',
                    options: materialDatabaseService.getBaseGeogridOptions(),
                    defaultValue: '', // No default geogrid selection (user must explicitly choose)
                    required: false,
                },
                {
                    name: 'effectivePorosity',
                    type: 'number',
                    label: 'effective_porosity_of_base_course',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().effectivePorosity,
                    isReadOnly: true,
                },
                {
                    name: 'permeabilityCoefficient',
                    type: 'number',
                    label: 'permeability_coefficient_of_base_course',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().permeabilityCoefficient,
                    isReadOnly: true,
                },
                {
                    name: 'cbrBaseCourse',
                    type: 'number',
                    label: 'cbr_base_course_label', // "CBR of base course"
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().cbr,
                    isReadOnly: true,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                {
                    name: 'ev2BaseCourse',
                    type: 'number',
                    label: 'ev2_base_course_label', // "Ev2 of base course"
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultBaseMaterial().ev2,
                    isReadOnly: true,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'ev2',
                    },
                },
            ],
            subbaseCourse: [
                {
                    name: 'subbaseMaterial',
                    type: 'string',
                    label: 'select_subbase_material',
                    renderAs: 'select',
                    options: materialDatabaseService.getSubbaseMaterialOptions(),
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().id,
                    required: true,
                    infoBoxTableData: {
                        headers: ['Material', 'Marshal Stability', 'CBR', 'Ev2', 'Layer Coefficient', 'N0', 'k'],
                        rows: [
                            ['Crushed gravel', '-', '90', '313.5', '0.14', '0.32', '5.00E-02'],
                            ['Mixed granular soil 1#', '-', '70', '266.9', '0.13', '0.32', '1.00E-04'],
                            ['Mixed granular soil 2#', '-', '50', '215.2', '0.12', '0.25', '5.00E-05'],
                            ['Mixed granular soil 3#', '-', '30', '155.2', '0.11', '0.3', '1.00E-05'],
                            ['Naturally stabilized mix 1#', '-', '20', '119.7', '0.1', '0.25', '5.00E-06'],
                            ['Naturally stabilized mix 2#', '-', '10', '76.8', '0.075', '0.2', '1.00E-06'],
                            ['Naturally stabilized mix 3#', '-', '5', '50.0', '0.05', '0.15', '5.00E-07'],
                        ],
                        caption: 'SUBBASE Material Properties and Coefficients',
                    },
                },
                {
                    name: 'minThickness',
                    type: 'number',
                    label: 'minimum_thickness_of_subbase_layer',
                    required: true,
                    defaultValue: 0.15,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'left',
                        companion: 'minThicknessSuggested',
                    },
                },
                {
                    name: 'minThicknessSuggested',
                    type: 'number',
                    label: 'minimum_thickness_suggested_subbase',
                    required: false,
                    defaultValue: 0.15,
                    unit: conversionLabel.m_ft,
                    isReadOnly: true,
                    symbol: 't',
                    isConvertible: true,
                    layout: {
                        inline: true,
                        width: '50%',
                        position: 'right',
                        companion: 'minThickness',
                    },
                    // Note: This field is calculated automatically based on AASHTO traffic classification from Design_LCR G26
                    // Uses DATABASE!K16 VLOOKUP logic with ESAL traffic ranges for minimum thickness requirements
                },
                {
                    name: 'layerCoefficienta3',
                    type: 'number',
                    label: 'layer_coefficient',
                    required: false,
                    defaultValue: (() => {
                        // Calculate the default value dynamically
                        const defaultMaterial = materialDatabaseService.getDefaultSubbaseMaterial();
                        return getLayerCoefficientForSubbaseMaterial(defaultMaterial.id);
                    })(),
                    isReadOnly: true,
                    calculation: calculateSubbaseLayerCoefficient,
                },
                {
                    name: 'subbaseGeogrid',
                    type: 'string',
                    label: 'select_geogrid_for_subbase_course',
                    renderAs: 'select',
                    options: materialDatabaseService.getSubbaseGeogridOptions(),
                    defaultValue: '', // No default geogrid selection (user must explicitly choose)
                    required: false,
                },
                {
                    name: 'effectivePorosity',
                    type: 'number',
                    label: 'effective_porosity_of_subbase_course',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().effectivePorosity,
                    isReadOnly: true,
                },
                {
                    name: 'permeabilityCoefficient',
                    type: 'number',
                    label: 'permeability_coefficient_of_subbase_course',
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().permeabilityCoefficient,
                    isReadOnly: true,
                },
                {
                    name: 'cbrSubbaseCourse',
                    type: 'number',
                    label: 'cbr_subbase_course_label', // "CBR of subbase course"
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().cbr,
                    isReadOnly: true,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                {
                    name: 'ev2SubbaseCourse',
                    type: 'number',
                    label: 'ev2_subbase_course_label', // "Ev2 of subbase course"
                    required: false,
                    defaultValue: materialDatabaseService.getDefaultSubbaseMaterial().ev2,
                    isReadOnly: true,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'ev2',
                    },
                },
            ],
            subgrade: [
                {
                    name: 'cbrSubgrade',
                    type: 'number',
                    label: 'cbr_subgrade_label', // "CBR of subgrade"
                    required: false,
                    defaultValue: 2,
                    min: 0,
                    max: 100,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                {
                    name: 'ev2Subgrade',
                    type: 'number',
                    label: 'ev2_subgrade_label', // "Ev2 of subgrade"
                    required: false,
                    defaultValue: 50,
                    min: 0,
                    max: 500,
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'ev2',
                    },
                },
            ],
            surfaceDesign: [
                {
                    name: 'enableSurfaceDesign',
                    type: 'string',
                    label: 'enable_surface_design_analysis',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: false,
                    mutuallyExclusiveGroup: ['enableSurfaceDesign', 'enableBaseDesign', 'enableSubbaseDesign'],
                },
                {
                    name: 'baseThickness',
                    type: 'number',
                    label: 'base_thickness_for_surface_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.2,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₂',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'baseCourse',
                    },
                    showWhen: {
                        field: 'enableSurfaceDesign',
                        value: 'yes',
                    },
                },
                {
                    name: 'subbaseThickness',
                    type: 'number',
                    label: 'subbase_thickness_for_surface_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.4,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₃',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'subbaseCourse',
                    },
                    showWhen: {
                        field: 'enableSurfaceDesign',
                        value: 'yes',
                    },
                },
            ],
            baseDesign: [
                {
                    name: 'enableBaseDesign',
                    type: 'string',
                    label: 'enable_base_design_analysis',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: false,
                    mutuallyExclusiveGroup: ['enableSurfaceDesign', 'enableBaseDesign', 'enableSubbaseDesign'],
                },
                {
                    name: 'surfaceThickness',
                    type: 'number',
                    label: 'surface_thickness_for_base_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.1,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₁',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'surface',
                    },
                    showWhen: {
                        field: 'enableBaseDesign',
                        value: 'yes',
                    },
                },
                {
                    name: 'subbaseThickness',
                    type: 'number',
                    label: 'subbase_thickness_for_base_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.4,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₃',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'subbaseCourse',
                    },
                    showWhen: {
                        field: 'enableBaseDesign',
                        value: 'yes',
                    },
                },
            ],
            subbaseDesign: [
                {
                    name: 'enableSubbaseDesign',
                    type: 'string',
                    label: 'enable_subbase_design_analysis',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                    ],
                    defaultValue: 'no',
                    required: false,
                    mutuallyExclusiveGroup: ['enableSurfaceDesign', 'enableBaseDesign', 'enableSubbaseDesign'],
                },
                {
                    name: 'surfaceThickness',
                    type: 'number',
                    label: 'surface_thickness_for_subbase_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.1,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₁',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'surface',
                    },
                    showWhen: {
                        field: 'enableSubbaseDesign',
                        value: 'yes',
                    },
                },
                {
                    name: 'baseThickness',
                    type: 'number',
                    label: 'base_thickness_for_subbase_design',
                    renderAs: 'slider',
                    required: true,
                    defaultValue: 0.2,
                    unit: conversionLabel.m_ft,
                    min: 0,
                    max: 2,
                    step: 0.05,
                    symbol: 'D₂',
                    isConvertible: true,
                    dynamicMin: {
                        sourceField: 'minThickness',
                        sourceGroup: 'baseCourse',
                    },
                    showWhen: {
                        field: 'enableSubbaseDesign',
                        value: 'yes',
                    },
                },
            ],
        },
    };
};

const getContainerYardBcr = (projectType: { type: string; method: string }): ProjectTypeJson => {
    return {
        projectType: projectType.type,
        projectMethod: projectType.method,
        fields: {
            projectSettings: [
                {
                    name: 'calculationMethod',
                    type: 'string',
                    renderAs: 'select',
                    label: 'calculation_method',
                    required: true,
                    defaultValue: 'bcr',
                    options: [
                        { value: 'bcr', label: 'BCR' },
                        { value: 'static', label: 'Static' },
                    ],
                    isConvertible: true,
                },
            ],
            load: [
                { name: 'deckWidth', type: 'number', label: 'deck_width', required: true, defaultValue: 1, unit: conversionLabel.m_ft, min: 0, max: 100, symbol: 'B', isConvertible: true },
                { name: 'deckLength', type: 'number', label: 'deck_length', required: true, defaultValue: 2, unit: conversionLabel.m_ft, min: 0, max: 100, symbol: 'L', isConvertible: true },
                { name: 'uniformLoad', type: 'number', label: 'uniform_load', required: true, defaultValue: 80, unit: conversionLabel.kPa_psf, min: 0, max: 1000, symbol: 'q', isConvertible: true },
            ],
            subgradeCharacteristics: [
                {
                    name: 'unitWeight',
                    type: 'number',
                    label: 'unit_weight',
                    required: true,
                    defaultValue: 10,
                    unit: conversionLabel.kNm3_lbfft3,
                    min: 0,
                    max: 100,
                    isConvertible: true,
                    symbol: 'γ',
                },
                { name: 'frictionAngle', type: 'number', label: 'friction_angle', required: true, defaultValue: 24, unit: conversionLabel.deg, min: 0, max: 100, isConvertible: true, symbol: 'φ' },
                { name: 'cohesion', type: 'number', label: 'cohesion', required: true, defaultValue: 0, unit: conversionLabel.kPa_psf, min: 0, max: 100, isConvertible: true, symbol: 'c' },
            ],
            embankmentCharacteristics: [
                {
                    name: 'unitWeight',
                    type: 'number',
                    label: 'unit_weight',
                    required: true,
                    defaultValue: 19,
                    unit: conversionLabel.kNm3_lbfft3,
                    min: 0,
                    max: 100,
                    isConvertible: true,
                    symbol: 'γ',
                },
                { name: 'frictionAngle', type: 'number', label: 'friction_angle', required: true, defaultValue: 30, unit: conversionLabel.deg, min: 0, max: 100, isConvertible: true, symbol: 'φ' },
                { name: 'cohesion', type: 'number', label: 'cohesion', required: true, defaultValue: 0, unit: conversionLabel.kPa_psf, min: 0, max: 100, isConvertible: true, symbol: 'c' },
                {
                    name: 'loadSpreadingAngleU',
                    type: 'number',
                    label: 'load_spreading_angle_u',
                    required: true,
                    defaultValue: 30,
                    unit: conversionLabel.deg,
                    min: 0,
                    max: 100,
                    isConvertible: true,
                    symbol: 'au',
                },
                {
                    name: 'loadSpreadingAngleR',
                    type: 'number',
                    label: 'load_spreading_angle_r',
                    required: true,
                    defaultValue: 45,
                    unit: conversionLabel.deg,
                    min: 0,
                    max: 100,
                    isConvertible: true,
                    symbol: 'ar',
                },
            ],
            fsDesign: [{ name: 'fsDesign', type: 'number', label: 'fsDesignLabel', required: true, defaultValue: 3, min: 0, max: 100, isConvertible: false, symbol: 'FS' }],
        },
    };
};

const getContainerYardStatic = (projectType: { type: string; method: string }): ProjectTypeJson => {
    return {
        projectType: projectType.type,
        projectMethod: projectType.method,
        fields: {
            projectSettings: [
                {
                    name: 'calculationMethod',
                    type: 'string',
                    renderAs: 'select',
                    label: 'Calculation Method',
                    required: true,
                    defaultValue: 'static',
                    options: [
                        { value: 'static', label: 'Static' },
                        { value: 'bcr', label: 'BCR' },
                    ],
                    isConvertible: true,
                },
            ],
            load: [
                { name: 'deckHalfWidth', type: 'number', label: 'deckHalfWidth', required: true, defaultValue: 1, unit: conversionLabel.m_ft, isConvertible: true, min: 0, max: 1000, symbol: 'B' },
                { name: 'deckHalfLength', type: 'number', label: 'deckHalfLength', required: true, defaultValue: 6, unit: conversionLabel.m_ft, isConvertible: true, min: 0, max: 1000, symbol: 'L' },
                { name: 'uniformLoad', type: 'number', label: 'uniformLoad', required: true, defaultValue: 100, unit: conversionLabel.kPa_psf, isConvertible: true, min: 0, max: 10000, symbol: 'q' },
            ],
            subgrade: [
                // 1. Campo per la selezione del tipo di parametro del suolo (specifico per Container Yard)
                {
                    name: 'subgradeInputMethod',
                    label: 'subgrade_input_method_label',
                    type: 'string',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'cbr', label: 'subgrade_option_cbr' },
                        { value: 'modulus', label: 'subgrade_option_modulus' },
                    ],
                    defaultValue: 'cbr',
                    required: true,
                },
                // 2. Campo CBR (nome originale per compatibilità API)
                {
                    name: 'cbrSubgrade',
                    type: 'number',
                    label: 'cbr_subgrade_label',
                    required: false,
                    defaultValue: 3,
                    unit: ['%'],
                    isConvertible: false,
                    min: 0,
                    max: 10,
                    symbol: 'CBR',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                // 3. Campo Modulo di Deformazione (nome originale per compatibilità API)
                {
                    name: 'deformationModulus',
                    type: 'number',
                    label: 'ev2_subgrade_label',
                    required: false,
                    defaultValue: 30,
                    unit: conversionLabel.mpa_psi,
                    isConvertible: true,
                    min: 0,
                    max: 500000,
                    symbol: 'Ev2',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'modulus',
                    },
                },
            ],
            fsDesign: [{ name: 'fsDesign', type: 'number', label: 'fsDesign', required: true, defaultValue: 3, isConvertible: false, min: 1, max: 10, symbol: 'FS' }],
        },
    };
};

const getParkingDeckBcr = (projectType: { type: string; method: string }): ProjectTypeJson => {
    return {
        projectType: projectType.type,
        projectMethod: projectType.method,
        fields: {
            projectSettings: [
                {
                    name: 'calculationMethod',
                    type: 'string',
                    renderAs: 'select',
                    label: 'calculation_method',
                    required: true,
                    defaultValue: 'bcr',
                    options: [
                        { value: 'bcr', label: 'BCR' },
                        { value: 'static', label: 'Static' },
                    ],
                    isConvertible: true,
                },
            ],
            load: [
                { name: 'wheelLoad', type: 'number', label: 'wheel_load', required: true, defaultValue: 826, unit: conversionLabel.kN_lbf, min: 0, max: 1500, symbol: 'P', isConvertible: true },
                {
                    name: 'tireInflationPressure',
                    type: 'number',
                    label: 'tire_inflation_pressure',
                    required: true,
                    defaultValue: 500,
                    unit: conversionLabel.kPa,
                    min: 0,
                    max: 1000,
                    symbol: 'p',
                    isConvertible: true,
                },
                /*
                {
                    name: 'wheelLoadDiameter',
                    type: 'number',
                    label: 'wheel_load_diameter',
                    required: true,
                    defaultValue: 1.184,
                    unit: conversionLabel.m_ft,
                    isConvertible: true,
                    min: 0,
                    max: 1000,
                    symbol: 'd',
                },
                {
                    name: 'wheelLoadRadius',
                    type: 'number',
                    label: 'wheel_load_radius',
                    required: true,
                    defaultValue: 0.592,
                    unit: conversionLabel.m_ft,
                    isConvertible: true,
                    min: 0,
                    max: 1000,
                    symbol: 'r',
                },
                */
            ],
            subgradeCharacteristics: [
                {
                    name: 'unitWeight',
                    type: 'number',
                    label: 'unit_weight',
                    required: true,
                    defaultValue: 17.5,
                    unit: conversionLabel.kNm3_lbfft3,
                    min: 0,
                    max: 100,
                    isConvertible: true,
                    symbol: 'γ',
                },
                { name: 'frictionAngle', type: 'number', label: 'friction_angle', required: true, defaultValue: 30, unit: conversionLabel.deg, min: 0, max: 100, isConvertible: true, symbol: 'φ' },
                { name: 'cohesion', type: 'number', label: 'cohesion', required: true, defaultValue: 0, unit: conversionLabel.kPa_psf, min: 0, max: 100, isConvertible: true, symbol: 'c' },
            ],
            embankmentCharacteristics: [
                {
                    name: 'unitWeight',
                    type: 'number',
                    label: 'unit_weight',
                    required: true,
                    defaultValue: 18,
                    unit: conversionLabel.kNm3_lbfft3,
                    min: 0,
                    max: 100,
                    isConvertible: true,
                    symbol: 'γ',
                },
                { name: 'frictionAngle', type: 'number', label: 'friction_angle', required: true, defaultValue: 35, unit: conversionLabel.deg, min: 0, max: 100, isConvertible: true, symbol: 'φ' },
                { name: 'cohesion', type: 'number', label: 'cohesion', required: true, defaultValue: 0, unit: conversionLabel.kPa_psf, min: 0, max: 100, isConvertible: true, symbol: 'c' },
                {
                    name: 'loadSpreadingAngleU',
                    type: 'number',
                    label: 'load_spreading_angle_u',
                    required: true,
                    defaultValue: 30,
                    unit: conversionLabel.deg,
                    min: 0,
                    max: 100,
                    isConvertible: true,
                    symbol: 'au',
                },
                {
                    name: 'loadSpreadingAngleR',
                    type: 'number',
                    label: 'load_spreading_angle_r',
                    required: true,
                    defaultValue: 45,
                    unit: conversionLabel.deg,
                    min: 0,
                    max: 100,
                    isConvertible: true,
                    symbol: 'ar',
                },
            ],
            fsDesign: [{ name: 'fsDesign', type: 'number', label: 'fsDesignLabel', required: true, defaultValue: 3, min: 0, max: 100, isConvertible: false, symbol: 'FS' }],
        },
    };
};

const getParkingDeckStatic = (projectType: { type: string; method: string }): ProjectTypeJson => {
    return {
        projectType: projectType.type,
        projectMethod: projectType.method,
        fields: {
            projectSettings: [
                {
                    name: 'calculationMethod',
                    type: 'string',
                    renderAs: 'select',
                    label: 'Calculation Method',
                    required: true,
                    defaultValue: 'static',
                    options: [
                        { value: 'static', label: 'Static' },
                        { value: 'bcr', label: 'BCR' },
                    ],
                    isConvertible: true,
                },
            ],
            load: [
                {
                    name: 'wheelLoad',
                    type: 'number',
                    label: 'wheel_load_standard_axle',
                    required: true,
                    defaultValue: 40,
                    unit: conversionLabel.kN_lbf,
                    min: 0,
                    max: 1500,
                    symbol: 'P',
                    isConvertible: true,
                },
                {
                    name: 'tireInflationPressure',
                    type: 'number',
                    label: 'tire_inflation_pressure',
                    required: true,
                    defaultValue: 500,
                    unit: conversionLabel.kPa,
                    min: 0,
                    max: 1500,
                    symbol: 'p',
                    isConvertible: true,
                },
                /*
                {
                    name: 'radiusContactArea',
                    type: 'number',
                    label: 'radius_contact_area',
                    required: true,
                    defaultValue: 0.146,
                    unit: conversionLabel.m_ft,
                    isConvertible: true,
                    min: 0,
                    max: 100,
                    symbol: 'r',
                },
                */
            ],
            subgrade: [
                // 1. Campo per la selezione del tipo di parametro del suolo (specifico per Parking Deck)
                {
                    name: 'subgradeInputMethod',
                    label: 'subgrade_input_method_label',
                    type: 'string',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'cbr', label: 'subgrade_option_cbr' },
                        { value: 'modulus', label: 'subgrade_option_modulus' },
                    ],
                    defaultValue: 'cbr',
                    required: true,
                },
                // 2. Campo CBR (nome originale per compatibilità API)
                {
                    name: 'cbrSubgrade',
                    type: 'number',
                    label: 'cbr_subgrade_label',
                    required: false,
                    defaultValue: 3,
                    unit: ['%'],
                    isConvertible: false,
                    min: 0,
                    max: 10,
                    symbol: 'CBR',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'cbr',
                    },
                },
                // 3. Campo Modulo di Deformazione (nome originale per compatibilità API)
                {
                    name: 'deformationModulus',
                    type: 'number',
                    label: 'ev2_subgrade_label',
                    required: false,
                    defaultValue: 30,
                    unit: conversionLabel.mpa_psi,
                    isConvertible: true,
                    min: 0,
                    max: 500000,
                    symbol: 'Ev2',
                    showWhen: {
                        field: 'subgradeInputMethod',
                        value: 'modulus',
                    },
                },
            ],
            fsDesign: [{ name: 'fsDesign', type: 'number', label: 'fsDesign', required: true, defaultValue: 3, isConvertible: false, min: 1, max: 10, symbol: 'FS' }],
        },
    };
};
//
// Parking Deck wheel load P goes up to 1500 kN and its GeoGrid starts from W = 2 × P, so W may reach 3000 kN there
const PARKING_DECK_MAX_DESIGN_AXLE_LOAD = 3000;
const DEFAULT_MAX_DESIGN_AXLE_LOAD = 1000;

export const getGeoGridJson = (isStandaloneMode = false, projectType?: string): GeoGridTypeJson => {
    const geogridItemFieldConfigs: FieldConfig[] = [
        {
            name: 'position',
            type: 'number',
            label: 'geogrid_position',
            // Every layer needs its height: the Ttot calculation reads it per layer (GeogridTtotService) and an empty one is not 0
            required: true,
            defaultValue: 0.1,
            unit: conversionLabel.m_ft,
            isConvertible: false,
            min: 0,
            infoBox: 'infobox_content_geogrid_position',
            infoBoxTitle: 'position_info_title',
        },
        {
            name: 'type',
            type: 'string',
            label: 'geogrid_type',
            renderAs: 'select',
            // TODO: Phase 3/4 - Use ConfigurationService.getGeogridDefaults().productFamily
            // Source of Truth: pavement-database.json designParameters.geogridDefaults.productFamily
            defaultValue: 'MacGrid WG',
            required: false,
            isConvertible: false,
            optionsSource: 'productFamily',
        },
        {
            name: 'designStrain',
            type: 'string',
            label: 'design_strain_percent',
            renderAs: 'select',
            required: false,
            defaultValue: '5',
            isConvertible: false,
            options: [
                { value: '1', label: '1%' },
                { value: '2', label: '2%' },
                { value: '3', label: '3%' },
                { value: '4', label: '4%' },
                { value: '5', label: '5%' },
            ],
        },
        {
            name: 'selectedGeogrid',
            type: 'string',
            label: 'selected_geogrid_product',
            renderAs: 'select',
            defaultValue: '',
            required: false,
            optionsSource: 'productItem',
        },
    ];

    return {
        projectType: 'GeoGrid',
        projectMethod: 'GeoGrid',
        accordionTitles: {
            type: 'geogrid_accordion_type',
            load: 'geogrid_accordion_load',
            truck: 'geogrid_accordion_truck',
            surfaceLayer: 'geogrid_accordion_surface_layer',
            baseCourse: 'geogrid_accordion_base_course',
            geogridBaseCourse: 'geogrid_accordion_geogrid_base_course',
            subBaseCourse: 'geogrid_accordion_subbase_course',
            geogridSubBaseCourse: 'geogrid_accordion_geogrid_subbase_course',
            subgrade: 'geogrid_accordion_subgrade',
        },
        fields: {
            type: [
                {
                    name: 'geogridType',
                    label: 'geogrid_type_label',
                    type: 'string',
                    renderAs: 'toggleButtonGroup',
                    options: [
                        { value: 'Rectangular', label: 'geogrid_option_rectangular' },
                        { value: 'Circular', label: 'geogrid_option_circular' },
                        { value: 'Railway', label: 'geogrid_option_railway' },
                    ],
                    defaultValue: 'Circular', // Default to most common case, will be overridden by project type logic
                    required: true,
                },
            ],
            load: [
                // Combined load fields
                // Fields for Rectangular type
                {
                    name: 'deckHalfWidth',
                    type: 'number',
                    label: 'deckHalfWidth',
                    required: true,
                    defaultValue: 0.75, // Will be dynamically set by form logic
                    unit: conversionLabel.m_ft,
                    isConvertible: true,
                    min: 0,
                    max: 1000, // Will be dynamically adjusted by form logic
                    symbol: 'B/2',
                    displayCondition: { field: 'type.geogridType', value: 'Rectangular' },
                },
                {
                    name: 'deckHalfLength',
                    type: 'number',
                    label: 'deckHalfLength',
                    required: true,
                    defaultValue: 6.25, // Will be dynamically set by form logic
                    unit: conversionLabel.m_ft,
                    isConvertible: true,
                    min: 0,
                    max: 1000, // Will be dynamically adjusted by form logic
                    symbol: 'L/2',
                    displayCondition: { field: 'type.geogridType', value: 'Rectangular' },
                },
                {
                    name: 'uniformLoad',
                    type: 'number',
                    label: 'uniformLoad',
                    required: true,
                    defaultValue: 80, // Will be dynamically set by form logic
                    unit: conversionLabel.kPa,
                    isConvertible: true,
                    min: 0,
                    max: 10000,
                    symbol: 'q',
                    displayCondition: { field: 'type.geogridType', value: 'Rectangular' },
                },
                // Fields for Circular type (formerly from loadWheel)
                {
                    name: 'designAxleLoad',
                    type: 'number',
                    label: 'designAxleLoad',
                    required: true,
                    defaultValue: 100,
                    unit: conversionLabel.kN_lbf,
                    isConvertible: true,
                    min: 0,
                    max: projectType === EProjectType.parking_deck ? PARKING_DECK_MAX_DESIGN_AXLE_LOAD : DEFAULT_MAX_DESIGN_AXLE_LOAD,
                    symbol: 'W',
                    displayCondition: { field: 'type.geogridType', value: 'Circular' },
                },
                {
                    name: 'tireInflationPressure',
                    type: 'number',
                    label: 'tireInflationPressure', // Consider more specific label
                    required: true,
                    defaultValue: 500,
                    unit: conversionLabel.kPa,
                    isConvertible: true,
                    min: 0,
                    max: 1000,
                    symbol: 'p',
                    displayCondition: { field: 'type.geogridType', value: 'Circular' },
                },
                {
                    name: 'equivalentStandardAxleLoad',
                    type: 'number',
                    label: 'equivalentStandardAxleLoad', // More specific label
                    required: true,
                    defaultValue: 80, // Default from original loadWheel config
                    unit: conversionLabel.kN_lbf,
                    isConvertible: true,
                    min: 0,
                    max: 1000, // Max from original loadWheel config
                    symbol: 'Pa',
                    displayCondition: { field: 'type.geogridType', value: 'Circular' },
                },
                // Note: wheelLoadDiameter from the original loadWheel group is omitted here.
                // If it's also needed for Circular type, add it with the appropriate displayCondition.

                // Railway type fields (shared base configuration, different defaults)
                {
                    name: 'deckHalfWidth',
                    type: 'number',
                    label: 'deckHalfWidth', // Same label for consistency
                    required: true,
                    defaultValue: 0.48, // Railway Excel value
                    unit: conversionLabel.m_ft,
                    isConvertible: true,
                    min: 0,
                    max: 1, // Railway specific max
                    symbol: 'B/2',
                    displayCondition: { field: 'type.geogridType', value: 'Railway' },
                },
                {
                    name: 'deckHalfLength',
                    type: 'number',
                    label: 'deckHalfLength', // Same label for consistency
                    required: true,
                    defaultValue: 0.15, // Railway Excel value
                    unit: conversionLabel.m_ft,
                    isConvertible: true,
                    min: 0,
                    max: 10, // Railway specific max
                    symbol: 'L/2',
                    displayCondition: { field: 'type.geogridType', value: 'Railway' },
                },
                {
                    name: 'uniformLoad',
                    type: 'number',
                    label: 'uniformLoad', // Same label for consistency
                    required: true,
                    defaultValue: 403, // Railway Excel value
                    unit: conversionLabel.kPa,
                    isConvertible: true,
                    min: 0,
                    max: 10000,
                    symbol: 'q',
                    displayCondition: { field: 'type.geogridType', value: 'Railway' },
                },
            ],
            truck: [
                { name: 'wheelRadius', type: 'number', label: 'wheelRadius', required: true, defaultValue: 0.15, unit: conversionLabel.m_ft, isConvertible: true, symbol: 'rw' },
                { name: 'tirePressure', type: 'number', label: 'tirePressure', required: true, defaultValue: 500, unit: conversionLabel.kPa, isConvertible: true, symbol: 'pw' },
                { name: 'thickness', type: 'number', label: 'thickness', required: true, defaultValue: 0.3, unit: conversionLabel.m_ft, isConvertible: true, symbol: 'h*' },
            ],
            surfaceLayer: [
                {
                    name: 'thickness',
                    type: 'number',
                    label: 'thickness',
                    required: true,
                    defaultValue: 0,
                    unit: conversionLabel.m_ft,
                    isConvertible: true,
                    min: 0,
                    max: 100,
                    symbol: 'h',
                    hideWhen: { field: 'type.geogridType', value: 'Railway' },
                },
                {
                    name: 'unitWeight',
                    type: 'number',
                    label: 'unitWeight',
                    required: true,
                    defaultValue: 24,
                    unit: conversionLabel.kNm3_lbfft3,
                    isConvertible: true,
                    symbol: 'γ',
                    hideWhen: { field: 'type.geogridType', value: 'Railway' },
                },
                {
                    name: 'loadSpreadingAngle',
                    type: 'number',
                    label: 'loadSpreadingAngle',
                    required: true,
                    defaultValue: 55,
                    unit: conversionLabel.deg,
                    isConvertible: true,
                    symbol: 'α',
                    hideWhen: { field: 'type.geogridType', value: 'Railway' },
                },
            ],
            baseCourse: [
                { name: 'unitWeight', type: 'number', label: 'unitWeight', required: true, defaultValue: 19, unit: conversionLabel.kNm3_lbfft3, isConvertible: true, min: 0, max: 100, symbol: 'γb' },
                { name: 'frictionAngle', type: 'number', label: 'frictionAngle', required: true, defaultValue: 30, unit: conversionLabel.deg, isConvertible: true, min: 0, max: 100, symbol: 'φb' },
                { name: 'cohesion', type: 'number', label: 'cohesion', required: true, defaultValue: 0, unit: conversionLabel.kPa, isConvertible: true, min: 0, max: 100, symbol: 'cb' },
                { name: 'loadSpreadingAngle', type: 'number', label: 'loadSpreadingAngle', required: true, defaultValue: 50, unit: conversionLabel.deg, isConvertible: true, min: 0, max: 90, symbol: 'αb' },
            ],
            geogridBaseCourse: [{ name: 'thickness', type: 'number', label: 'thickness', required: true, defaultValue: 0.4, unit: conversionLabel.m_ft, isConvertible: true, min: 0, max: 100 }],
            subBaseCourse: [
                { name: 'unitWeight', type: 'number', label: 'unitWeight', required: true, defaultValue: 19, unit: conversionLabel.kNm3_lbfft3, isConvertible: true, min: 0, max: 100, symbol: 'γb' },
                { name: 'frictionAngle', type: 'number', label: 'frictionAngle', required: true, defaultValue: 30, unit: conversionLabel.deg, isConvertible: true, min: 0, max: 100, symbol: 'φb' },
                { name: 'cohesion', type: 'number', label: 'cohesion', required: true, defaultValue: 0, unit: conversionLabel.kPa, isConvertible: true, min: 0, max: 100, symbol: 'cb' },
                { name: 'loadSpreadingAngle', type: 'number', label: 'loadSpreadingAngle', required: true, defaultValue: 50, unit: conversionLabel.deg, isConvertible: true, min: 0, max: 90, symbol: 'αb' },
            ],
            geogridSubBaseCourse: [{ name: 'thickness', type: 'number', label: 'thickness', required: false, defaultValue: 0, unit: conversionLabel.m_ft, isConvertible: true, min: 0 }],
            subgrade: [
                // Keep original key 'subGrade' if intended
                // 1. Add Input Method Selector
                {
                    name: 'subgradeInputMethod',
                    label: 'subgrade_input_method_label', // e.g., "Subgrade Input Method"
                    type: 'string',
                    renderAs: 'toggleButtonGroup', // or 'select'
                    options: [
                        { value: 'cbr', label: 'subgrade_option_cbr' }, // e.g., "CBR (%)"
                        { value: 'modulus', label: 'subgrade_option_modulus' }, // e.g., "Deformation Modulus (kPa)"
                    ],
                    defaultValue: 'cbr',
                    required: true,
                },
                // 2. Modify CBR Field
                {
                    name: 'cbrSubgrade',
                    type: 'number',
                    label: 'cbrSubgrade_label', // e.g., "CBR"
                    required: false, // Now conditional
                    defaultValue: 1, // Sensible default CBR?
                    unit: ['%'], // CBR is typically %
                    isConvertible: false, // CBR isn't typically converted
                    min: 0,
                    max: 100, // Max CBR is usually 100
                    symbol: 'CBR',
                },
                // 3. Add Deformation Modulus Field
                {
                    name: 'deformationModulus',
                    type: 'number',
                    label: 'deformationModulus_label', // e.g., "Deformation Modulus"
                    required: false, // Now conditional
                    defaultValue: 30,
                    unit: conversionLabel.mpa_psi,
                    isConvertible: true,
                    min: 0,
                    max: 500000, // Adjust max
                    symbol: 'Ev2', // Or Evd, E2 etc.
                },
                // 4. Keep Safety Factor (always visible and required)
                {
                    name: 'safetyFactor',
                    type: 'number',
                    label: 'safety_factor',
                    required: true,
                    defaultValue: 3,
                    isConvertible: false,
                    symbol: 'FS',
                    min: 1,
                    max: 10,
                },
            ],

            // Ora 'geogridItem' contiene solo la DEFINIZIONE dei campi item e non i dati
            geogridItem: geogridItemFieldConfigs,
        },
        // Conditional hiddenGroups based on isStandaloneMode
        // In standalone mode (direct geogrid creation), show the type field
        // In project-based mode (from project results), hide the type field as it's automatically determined
        hiddenGroups: isStandaloneMode ? [] : ['type'],
    };
};

export const defaultProjectMethod: Record<string, string[]> = {
    paved: ['asphalt'],
    unpaved: ['giroud', 'leng'],
    container_yard: ['bcr'],
    parking_deck: ['bcr'],
    railways: ['default'],
    airport: ['asphalt'],
};

/**
 * Get geogrid products data, optionally filtered by country and/or project type.
 * Uses the unified ProductDataService for data access with geolocation and project compatibility filtering.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "IT")
 * @param projectType - Project type (e.g., "airport", "railways", "paved", "unpaved", "container_yard", "parking_deck")
 * @returns Promise<TGeogridProductData> - Products grouped by family, filtered by compatibility
 */
export const getGeogridProductsDataAsync = async (
    countryCode?: string,
    projectType?: 'airport' | 'paved' | 'unpaved' | 'railways' | 'container_yard' | 'parking_deck'
): Promise<TGeogridProductData> => {
    // 1. If no filters provided, return all (backward compatibility)
    if (!countryCode && !projectType) {
        logDebug('ProjectTypeService', 'No filters provided, returning all product families from unified data');
        await new Promise(resolve => setTimeout(resolve, 50));
        return ProductDataService.getAllProducts();
    }

    // 2. If only projectType is provided (no country filtering)
    if (!countryCode && projectType) {
        logDebug('ProjectTypeService', `Filtering products by project type only: ${projectType}`);
        const filtered = ProductDataService.getProductsWithFilters({ projectType });

        const totalFamiliesFiltered = Object.keys(filtered).length;
        const totalProductsFiltered = Object.values(filtered).reduce((sum, products) => sum + products.length, 0);

        logDebug('ProjectTypeService', `Filtering complete: ${totalFamiliesFiltered} families, ${totalProductsFiltered} products`, {
            families: Object.keys(filtered),
        });

        return filtered;
    }

    // 3. Fetch allowed families from backend (mock endpoint) and apply both country and project type filters
    try {
        logDebug('ProjectTypeService', `Fetching allowed geogrid families for country: ${countryCode}`, { projectType });
        const response = await BeMaccaferriStoreHelper.getAvailableGeogridFamiliesAsync(countryCode!);

        if (!response.data || response.data.length === 0) {
            // Fallback: apply project type filter only if available
            logWarn('ProjectTypeService', 'No families returned for country, applying project type filter to all products', { countryCode, projectType });
            return projectType ? ProductDataService.getProductsWithFilters({ projectType }) : ProductDataService.getAllProducts();
        }

        logDebug('ProjectTypeService', `Received ${response.data.length} allowed families from API`, {
            families: response.data.map((f: IGeogridFamily) => f.name),
        });

        // 4. Apply both geolocation and project type filters
        let filtered = ProductDataService.getProductsByCountry(countryCode!, response.data);

        // Apply additional project type filter if provided
        if (projectType) {
            logDebug('ProjectTypeService', `Applying additional project type filter: ${projectType}`);
            const familiesBeforeFilter = Object.keys(filtered);

            filtered = ProductDataService.getProductsWithFilters({
                projectType,
                countryCode,
            });

            // Filter only families that were allowed by country
            const allowedFamilyNames = new Set(familiesBeforeFilter);
            filtered = Object.fromEntries(Object.entries(filtered).filter(([familyName]) => allowedFamilyNames.has(familyName)));
        }

        const totalFamiliesFiltered = Object.keys(filtered).length;
        const totalProductsFiltered = Object.values(filtered).reduce((sum, products) => sum + products.length, 0);

        logDebug('ProjectTypeService', `Filtering complete (unified data): ${totalFamiliesFiltered} families, ${totalProductsFiltered} products`, {
            families: Object.keys(filtered),
            projectType,
        });

        return filtered;
    } catch (error) {
        // Fallback on error: apply project type filter if available
        logError('ProjectTypeService', 'Error fetching geogrid families, applying available filters', error);
        return projectType ? ProductDataService.getProductsWithFilters({ projectType }) : ProductDataService.getAllProducts();
    }
};

/**
 * Helper function to get calculated surface properties when material selection changes
 * This should be called from form components when surfaceMaterial field changes
 *
 * IMPORTANT: layerCoefficient is a calculated/derived value, not user input
 * It should be automatically populated in the UI based on the selected surfaceMaterial
 */
export const getSurfacePropertiesForMaterial = (materialId: string) => {
    const material = materialDatabaseService.getSurfaceMaterialById(materialId);
    if (!material) {
        return {
            layerCoefficient: 0.44, // Default fallback for calculations
            minThickness: 0.05, // Value in meters (converted from 50mm)
        };
    }

    return {
        layerCoefficient: material.layerCoefficient, // CALCULATED: Auto-populated from material selection
        minThickness: material.minThickness,
        suggestedThickness: material.suggestedThickness,
    };
};

/**
 * Get layer coefficient for a specific surface material (for calculations only)
 * This is used by the calculation engine to get the coefficient based on selected material
 */
export const getLayerCoefficientForSurfaceMaterial = (materialId: string): number => {
    const coefficient = materialDatabaseService.getSurfaceLayerCoefficient(materialId);
    return coefficient ?? 0.44; // Default fallback
};

/**
 * Calculate minimum suggested thickness for surface layer based on ESAL traffic load
 * Implements AASHTO table logic as per Excel formula: =VLOOKUP(0,I7:J12,2,)
 * Based on DATABASE sheet logic where thickness is determined by ESAL ranges
 */
export const getMinimumSuggestedThicknessForSurface = (esalValue: number): number => {
    // Use MaterialDatabaseService for accurate ESAL-based thickness calculation
    // This ensures consistency with Excel DATABASE.txt Table 24 (Asphalt Concrete)
    const minThickness = materialDatabaseService.getMinThicknessForESALByLayer(esalValue, 'surface');

    if (minThickness !== undefined) {
        return minThickness;
    }

    // Default fallback for very low traffic
    return 0.025; // Updated to match Excel DATABASE.txt minimum
};

/**
 * Calculate minimum suggested thickness for base course based on ESAL values
 * Uses DATABASE.txt Table 26 (Aggregate Base) for accurate thickness calculation
 * Based on AASHTO traffic load classification and minimum thickness requirements
 */
export const getMinimumSuggestedThicknessForBase = (esalValue: number): number => {
    // ESAL-based thickness calculation from DATABASE.txt Table 26 (Aggregate Base)
    // This follows the Excel database logic exactly without LCR calculations

    if (esalValue <= 0) {
        // Return default minimum for invalid inputs
        return 0.1; // 100mm default minimum base thickness
    }

    // Use MaterialDatabaseService for accurate ESAL-based thickness calculation
    // This ensures consistency with Excel DATABASE.txt Table 26 (Aggregate Base)
    const minThickness = materialDatabaseService.getMinThicknessForESALByLayer(esalValue, 'base');

    if (minThickness !== undefined) {
        return minThickness;
    }

    // Default fallback for invalid inputs - matches Excel DATABASE.txt minimum
    return 0.1; // 100mm minimum base thickness
};

/**
 * Calculate minimum suggested thickness for subbase course based on ESAL values
 * Implements Design_LCR G26 logic which uses DATABASE!K16 VLOOKUP
 * Based on AASHTO traffic load classification and minimum thickness requirements
 */
export const getMinimumSuggestedThicknessForSubbase = (esalValue: number): number => {
    // Use MaterialDatabaseService for accurate ESAL-based thickness calculation
    // This ensures consistency with Excel DATABASE.txt Table 26 (Aggregate Base)
    const minThickness = materialDatabaseService.getMinThicknessForESALByLayer(esalValue, 'subbase');

    if (minThickness !== undefined) {
        return minThickness;
    }

    // Default fallback for invalid inputs
    return 0.1; // Updated to match Excel DATABASE.txt minimum
};

/**
 * Get surface material by ID (for form validation and display)
 */
export const getSurfaceMaterialById = (materialId: string) => {
    return materialDatabaseService.getSurfaceMaterialById(materialId);
};

/**
 * Get all surface material options (for form field options)
 */
export const getSurfaceMaterialOptions = () => {
    return materialDatabaseService.getSurfaceMaterialOptions();
};

/**
 * BASE MATERIAL HELPER FUNCTIONS
 */

/**
 * Get base material properties for a specific material ID
 * Used to populate form fields when base material changes
 */
export const getBasePropertiesForMaterial = (materialId: string) => {
    const material = materialDatabaseService.getBaseMaterialById(materialId);

    if (!material) {
        // Return default values if material not found
        return {
            layerCoefficient: 0.12, // Default fallback for calculations
            minThickness: 150,
            cbr: 70,
            ev2: 266.92,
            effectivePorosity: 0.32,
            permeabilityCoefficient: 8.46,
        };
    }

    return {
        layerCoefficient: material.layerCoefficient, // CALCULATED: Auto-populated from material selection
        minThickness: material.minThickness,
        cbr: material.cbr,
        ev2: material.ev2,
        effectivePorosity: material.effectivePorosity,
        permeabilityCoefficient: material.permeabilityCoefficient,
    };
};

/**
 * Get layer coefficient for a specific base material (for calculations only)
 */
export const getLayerCoefficientForBaseMaterial = (materialId: string): number => {
    const coefficient = materialDatabaseService.getBaseLayerCoefficient(materialId);
    return coefficient ?? 0.12; // Default fallback
};

/**
 * Get CBR for a specific base material
 */
export const getCBRForBaseMaterial = (materialId: string): number => {
    const cbr = materialDatabaseService.getBaseCBR(materialId);
    return cbr ?? 70; // Default fallback
};

/**
 * Get Ev2 for a specific base material
 */
export const getEv2ForBaseMaterial = (materialId: string): number => {
    const ev2 = materialDatabaseService.getBaseEv2(materialId);
    return ev2 ?? 266.92; // Default fallback
};

/**
 * Get effective porosity for a specific base material
 */
export const getEffectivePorosityForBaseMaterial = (materialId: string): number => {
    const porosity = materialDatabaseService.getBaseEffectivePorosity(materialId);
    return porosity ?? 0.32; // Default fallback
};

/**
 * Get permeability coefficient for a specific base material
 */
export const getPermeabilityCoefficientForBaseMaterial = (materialId: string): number => {
    const permeability = materialDatabaseService.getBasePermeabilityCoefficient(materialId);
    return permeability ?? 8.46; // Default fallback
};

/**
 * Get base material by ID (for form validation and display)
 */
export const getBaseMaterialById = (materialId: string) => {
    return materialDatabaseService.getBaseMaterialById(materialId);
};

/**
 * Get all base material options (for form field options)
 */
export const getBaseMaterialOptions = () => {
    return materialDatabaseService.getBaseMaterialOptions();
};

/**
 * SUBBASE MATERIAL HELPER FUNCTIONS
 */

/**
 * Get subbase material properties for a specific material ID
 * Used to populate form fields when subbase material changes
 */
export const getSubbasePropertiesForMaterial = (materialId: string) => {
    const material = materialDatabaseService.getSubbaseMaterialById(materialId);

    if (!material) {
        // Return default values if material not found
        return {
            layerCoefficient: 0.085, // Default fallback for calculations
            minThickness: 150,
            cbr: 15,
            ev2: 100,
            effectivePorosity: 0.25,
            permeabilityCoefficient: 1.0,
        };
    }

    return {
        layerCoefficient: material.layerCoefficient, // CALCULATED: Auto-populated from material selection
        minThickness: material.minThickness,
        cbr: material.cbr,
        ev2: material.ev2,
        effectivePorosity: material.effectivePorosity,
        permeabilityCoefficient: material.permeabilityCoefficient,
    };
};

/**
 * Get layer coefficient for a specific subbase material (for calculations only)
 */
export const getLayerCoefficientForSubbaseMaterial = (materialId: string): number => {
    const coefficient = materialDatabaseService.getSubbaseLayerCoefficient(materialId);
    return coefficient ?? 0.14; // Default fallback (crushed gravel)
};

/**
 * Get CBR for a specific subbase material
 */
export const getCBRForSubbaseMaterial = (materialId: string): number => {
    const cbr = materialDatabaseService.getSubbaseCBR(materialId);
    return cbr ?? 90; // Default fallback (crushed gravel)
};

/**
 * Get Ev2 for a specific subbase material
 */
export const getEv2ForSubbaseMaterial = (materialId: string): number => {
    const ev2 = materialDatabaseService.getSubbaseEv2(materialId);
    return ev2 ?? 313.49; // Default fallback (crushed gravel)
};

/**
 * Get effective porosity for a specific subbase material
 */
export const getEffectivePorosityForSubbaseMaterial = (materialId: string): number => {
    const porosity = materialDatabaseService.getSubbaseEffectivePorosity(materialId);
    return porosity ?? 0.32; // Default fallback (crushed gravel)
};

/**
 * Get permeability coefficient for a specific subbase material
 */
export const getPermeabilityCoefficientForSubbaseMaterial = (materialId: string): number => {
    const permeability = materialDatabaseService.getSubbasePermeabilityCoefficient(materialId);
    return permeability ?? 0.05; // Default fallback (crushed gravel)
};

/**
 * Get subbase material by ID (for form validation and display)
 */
export const getSubbaseMaterialById = (materialId: string) => {
    return materialDatabaseService.getSubbaseMaterialById(materialId);
};

/**
 * Get all subbase material options (for form field options)
 */
export const getSubbaseMaterialOptions = () => {
    return materialDatabaseService.getSubbaseMaterialOptions();
};

/**
 * Format numbers to maximum 2 decimal places for calculated fields
 * @param value - The number to format
 * @returns Number with maximum 2 decimal places
 */
const formatToTwoDecimals = (value: number | undefined): number => {
    if (value === undefined || value === null) return value || 0;
    return Math.round(value * 100) / 100;
};

/**
 * Excel VLOOKUP logic implementation for dynamic field updates
 * When base material changes (G11), all related fields should update automatically
 */

/**
 * Get all dependent fields that should update when base material changes
 * Excel Logic: VLOOKUP updates G12, G18, G19, G21, G22 when G11 changes
 */
export const getBaseMaterialDependentFields = (
    baseMaterialId: string
): {
    layerCoefficienta2: number;
    effectivePorosity: number;
    permeabilityCoefficient: number;
    cbrBaseCourse: number;
    ev2BaseCourse: number;
    drainageQuality?: string;
    lcrValue?: number;
} => {
    const material = materialDatabaseService.getBaseMaterialById(baseMaterialId);

    if (!material) {
        return {
            layerCoefficienta2: 0.12, // ⚠️ CRITICAL: DO NOT round layer coefficients - Excel requires 3 decimal precision
            effectivePorosity: formatToTwoDecimals(0.32),
            permeabilityCoefficient: formatToTwoDecimals(8.46),
            cbrBaseCourse: formatToTwoDecimals(70),
            ev2BaseCourse: formatToTwoDecimals(266.9),
        };
    }

    return {
        layerCoefficienta2: material.layerCoefficient, // ⚠️ CRITICAL: DO NOT round - Excel G12 = VLOOKUP(G11, DATABASE, layer_coeff_column)
        effectivePorosity: formatToTwoDecimals(material.effectivePorosity), // Excel G18 = VLOOKUP(G11, DATABASE, porosity_column)
        permeabilityCoefficient: formatToTwoDecimals(material.permeabilityCoefficient), // Excel G19 = VLOOKUP(G11, DATABASE, permeability_column)
        cbrBaseCourse: formatToTwoDecimals(material.cbr), // Excel G21 = VLOOKUP(G11, DATABASE, cbr_column)
        ev2BaseCourse: formatToTwoDecimals(material.ev2), // Excel G22 = VLOOKUP(G11, DATABASE, ev2_column)
        drainageQuality: material.drainageQuality, // For drainage coefficient calculations
        lcrValue: formatToTwoDecimals(material.lcrValue), // For stabilized analysis (G85)
    };
};

/**
 * Get all dependent fields that should update when subbase material changes
 * Excel Logic: Similar VLOOKUP pattern for subbase material selection
 */
export const getSubbaseMaterialDependentFields = (
    subbaseMaterialId: string
): {
    layerCoefficienta3: number;
    effectivePorosity: number;
    permeabilityCoefficient: number;
    cbrSubbaseCourse: number;
    ev2SubbaseCourse: number;
    drainageQuality?: string;
    lcrValue?: number;
} => {
    const material = materialDatabaseService.getSubbaseMaterialById(subbaseMaterialId);

    if (!material) {
        return {
            layerCoefficienta3: 0.075, // ⚠️ CRITICAL: DO NOT round layer coefficients - Excel requires 3 decimal precision
            effectivePorosity: formatToTwoDecimals(0.2),
            permeabilityCoefficient: formatToTwoDecimals(0.0846),
            cbrSubbaseCourse: formatToTwoDecimals(10),
            ev2SubbaseCourse: formatToTwoDecimals(76.83),
        };
    }

    // Use single source of truth for layer coefficient (pavement-database.json)
    const layerCoefficient = materialDatabaseService.getSubbaseLayerCoefficient(subbaseMaterialId) || material.layerCoefficient;

    return {
        layerCoefficienta3: layerCoefficient, // ⚠️ CRITICAL: DO NOT round layer coefficients - Excel requires 3 decimal precision (0.075 not 0.08)
        effectivePorosity: formatToTwoDecimals(material.effectivePorosity),
        permeabilityCoefficient: formatToTwoDecimals(material.permeabilityCoefficient),
        cbrSubbaseCourse: formatToTwoDecimals(material.cbr),
        ev2SubbaseCourse: formatToTwoDecimals(material.ev2),
        drainageQuality: material.drainageQuality,
        lcrValue: formatToTwoDecimals(material.lcrValue),
    };
};

/**
 * Get all dependent fields that should update when surface material changes
 * Excel Logic: VLOOKUP for surface material properties
 */
export const getSurfaceMaterialDependentFields = (
    surfaceMaterialId: string
): {
    layerCoefficienta1: number;
    minThickness: number;
    suggestedThickness?: number;
    materialType?: string;
} => {
    const material = materialDatabaseService.getSurfaceMaterialById(surfaceMaterialId);

    if (!material) {
        return {
            layerCoefficienta1: 0.44,
            minThickness: 0.05,
        };
    }

    return {
        layerCoefficienta1: material.layerCoefficient,
        minThickness: material.minThickness,
        suggestedThickness: material.suggestedThickness,
        materialType: material.materialType,
    };
};

/**
 * Calculate cascade effects when base material changes
 * Excel Logic: G12 → G58 → G87 (a2 × LCR), G21 → N20 → G53/C90 (CBR → Ebc)
 */
export const calculateBaseMaterialCascadeEffects = (
    baseMaterialId: string,
    baseGeogridId: string | null = null
): {
    // Direct VLOOKUP effects
    baseProperties: ReturnType<typeof getBaseMaterialDependentFields>;

    // Cascade effects (Design_LCR propagation)
    unstabilizedCoefficient: number; // G58 = G12 (unstabilized coefficient equals layer coefficient)
    stabilizedCoefficient: number; // G87 = G58 × G85 (LCR)
    designCBR: number; // N20 (from G21)
    baseModulus: number; // G53/C90: Base modulus calculation from CBR

    // Geogrid effects
    lcrValue: number; // G85 from geogrid selection
    geogridEnabled: boolean; // Based on material structural validity
} => {
    const baseProperties = getBaseMaterialDependentFields(baseMaterialId);

    // Excel G58 = G12 (unstabilized coefficient equals layer coefficient)
    const unstabilizedCoefficient = baseProperties.layerCoefficienta2;

    // Excel G85: LCR value from geogrid selection or material default
    const lcrValue = materialDatabaseService.getConditionalLCRValue(baseGeogridId, 'base');

    // Excel G87 = G58 × G85 (stabilized coefficient = unstabilized × LCR)
    const stabilizedCoefficient = unstabilizedCoefficient * lcrValue;

    // Excel N20: Design CBR (can be calculated or direct from material)
    const designCBR = baseProperties.cbrBaseCourse;

    // Excel G53/C90: Base modulus calculation from CBR
    // Formula: IF(CBR<=5, CBR*10*1000, 17.621*(CBR^0.64)*1000) in kPa
    const baseModulus = materialDatabaseService.calculateSubgradeResilientModulusKPa(designCBR);

    // Check if geogrid selection should be enabled
    const geogridEnabled = materialDatabaseService.isBaseGeogridSelectionEnabled(baseMaterialId);

    return {
        baseProperties,
        unstabilizedCoefficient,
        stabilizedCoefficient,
        designCBR,
        baseModulus,
        lcrValue,
        geogridEnabled,
    };
};

/**
 * Calculate cascade effects when subbase material changes
 * Similar to base material but for subbase layer
 */
export const calculateSubbaseMaterialCascadeEffects = (
    subbaseMaterialId: string,
    subbaseGeogridId: string | null = null
): {
    subbaseProperties: ReturnType<typeof getSubbaseMaterialDependentFields>;
    unstabilizedCoefficient: number;
    stabilizedCoefficient: number;
    designCBR: number;
    subbaseModulus: number;
    lcrValue: number;
    geogridEnabled: boolean;
} => {
    const subbaseProperties = getSubbaseMaterialDependentFields(subbaseMaterialId);

    const unstabilizedCoefficient = subbaseProperties.layerCoefficienta3;
    const lcrValue = materialDatabaseService.getConditionalLCRValue(subbaseGeogridId, 'subbase');
    const stabilizedCoefficient = unstabilizedCoefficient * lcrValue;
    const designCBR = subbaseProperties.cbrSubbaseCourse;
    const subbaseModulus = materialDatabaseService.calculateSubgradeResilientModulusKPa(designCBR);
    const geogridEnabled = materialDatabaseService.isSubbaseGeogridSelectionEnabled(subbaseMaterialId);

    return {
        subbaseProperties,
        unstabilizedCoefficient,
        stabilizedCoefficient,
        designCBR,
        subbaseModulus,
        lcrValue,
        geogridEnabled,
    };
};

/**
 * Calculate surface layer coefficient based on selected material
 * This ensures the UI displays the correct coefficient when material changes
 */
export const calculateSurfaceLayerCoefficient = (data: unknown): number => {
    const formData = data as { surfaceMaterial?: string };
    // If data is not available or surfaceMaterial is not set, get the default material ID
    const materialId = formData?.surfaceMaterial || materialDatabaseService.getDefaultSurfaceMaterial().id;

    return getLayerCoefficientForSurfaceMaterial(materialId);
};

/**
 * Calculate base layer coefficient based on selected material
 * This ensures the UI displays the correct coefficient when material changes
 */
export const calculateBaseLayerCoefficient = (data: unknown): number => {
    const formData = data as { baseMaterial?: string };
    // If data is not available or baseMaterial is not set, get the default material ID
    const materialId = formData?.baseMaterial || materialDatabaseService.getDefaultBaseMaterial().id;

    return getLayerCoefficientForBaseMaterial(materialId);
};

/**
 * Calculate subbase layer coefficient based on selected material
 * This ensures the UI displays the correct coefficient when material changes
 */
export const calculateSubbaseLayerCoefficient = (data: unknown): number => {
    const formData = data as { subbaseMaterial?: string };
    // If data is not available or subbaseMaterial is not set, get the default material ID
    const materialId = formData?.subbaseMaterial || materialDatabaseService.getDefaultSubbaseMaterial().id;

    return getLayerCoefficientForSubbaseMaterial(materialId);
};

/**
 * Calculate reliability level suggested based on functional classification and road type
 * Excel Logic: VLOOKUP(D14,DATABASE!B7:D10,IF(D15=DATABASE!C6,2,IF(D15=DATABASE!D6,3,)),)
 * Implements Excel D16 calculation from 1_Road_Info sheet using TrafficClassificationService
 */
export const calculateReliabilityLevelSuggested = (data: unknown): string => {
    const formData = data as { functionalClassification?: string; roadType?: string };
    if (!formData || !formData.functionalClassification || !formData.roadType) {
        return '';
    }

    const { range } = TrafficClassificationService.getReliabilityRange(formData.functionalClassification, formData.roadType);
    return range;
};

/**
 * Calculate tensile strength at 2% strain based on selected geogrid product
 * This function looks up the tensile_2_percent value from unified product data
 * for the selected geogrid product in railway projects
 */
export const calculateTensileStrengthAt2Percent = (data: unknown): number => {
    const formData = data as { selectedGeogrid?: string; geoGrid?: { selectedGeogrid?: string } };
    // Extract selectedGeogrid from data
    const selectedGeogrid = formData?.selectedGeogrid || formData?.geoGrid?.selectedGeogrid;

    if (!selectedGeogrid || selectedGeogrid === 'none_selected') {
        return 11.0; // Default value
    }

    // Normalize the input for comparison (trim and uppercase for consistent matching)
    const normalizedInput = selectedGeogrid.trim().toUpperCase();

    // Get all products from ProductDataService
    const allProducts = ProductDataService.getAllProducts();

    // Search through all geogrid families to find the matching product
    for (const familyKey in allProducts) {
        const family = allProducts[familyKey];
        if (Array.isArray(family)) {
            const product = family.find(item => {
                // Normalize the product name for case-insensitive comparison
                const normalizedProduct = item.prodotto.trim().toUpperCase();
                return normalizedProduct === normalizedInput;
            });
            if (product && product.tensile_2_percent !== null) {
                return product.tensile_2_percent;
            }
        }
    }

    // If product not found, return default value
    return 11.0;
};

/**
 * Calculate cascade effects when railway geogrid changes
 * Follows the same pattern as base/subbase material cascade
 */
export const calculateRailwayGeogridCascadeEffects = (
    selectedGeogrid: string
): {
    tensileStrengthAt2Percent: number;
} => {
    // Use existing calculation function
    const tensileStrengthAt2Percent = calculateTensileStrengthAt2Percent({ selectedGeogrid });

    return {
        tensileStrengthAt2Percent,
    };
};
